import { findChatById } from '@/lib/chatIndex';
import ExperienceCard from './ExperienceCard';
import { EXPERIENCE_KINDS, parseExperienceSpec } from '@/constants/utils/experience_spec';
import React, { useContext, useMemo, useCallback, useEffect, useRef, useState } from 'react';
import { StyleSheet, Text, TextInput, View, Platform, Linking, Pressable, ScrollView } from 'react-native';
import { SideChatContext } from '@/components/Chat/SideChatContext';
import MessageSelectionView from './MessageSelectionView';
import * as FileSystem from 'expo-file-system';
import * as Sharing from 'expo-sharing';
import { LinearGradient } from 'expo-linear-gradient';
import Markdown, { MarkdownIt } from 'react-native-markdown-display';
import InlineHtmlPreview from './InlineHtmlPreview';
import ProjectDiffLines from '@/components/Chat/ProjectDiffLines';
import { parseProjectGitDiff } from '@/lib/projectChangeDiff';
import { macMarkdownStyles } from './macMarkdownStyles';
import useMacFontScale from '@/hooks/useMacFontScale';
import { scaleTextStyles } from '@/constants/macFontScale';
import { workMarkdownStyles } from './workMarkdownStyles';
import { tableColumnWidths } from './tableColumnWidths';
import useProjectSourceReference from '@/hooks/useProjectSourceReference';
import useWorkArtifactReference from '@/hooks/useWorkArtifactReference';
import { coworkerApi } from '@/lib/api';
import { coworkerArtifactAsFile } from '@/components/Coworker/coworkerArtifactTypes';
import { hydrateOutputFileForOpen } from '@/constants/utils/conversation_outputs_download';

// Preserve the user's/model's literal punctuation: typography substitutions
// corrupt unfenced JSON, command flags, and text copied from the rendered page.
const markdownIt = MarkdownIt({ typographer: false, linkify: true, breaks: true });
// .py and .md are also country domains. In chat, bare source filenames should
// remain literal; explicit URLs, www addresses and Markdown links still work.
markdownIt.core.ruler.after('linkify', 'literal_source_filenames', state => {
  for (const block of state.tokens) {
    if (block.type !== 'inline' || !block.children) continue;
    const tokens = block.children;
    for (let i = tokens.length - 3; i >= 0; i--) {
      const [open, text, close] = tokens.slice(i, i + 3);
      if (open.type !== 'link_open' || open.markup !== 'linkify' || open.info !== 'auto'
          || text.type !== 'text' || close.type !== 'link_close'
          || !/^[\w.-]+\.(?:py|md)$/i.test(text.content) || /^www\./i.test(text.content)) continue;
      text.level = open.level;
      tokens.splice(i, 3, text);
    }
  }
});
// Some model output double-escapes entities, e.g. "&amp;#x0393;". Decode at
// the source token, before markdown-it's normal entity rule: escaped ampersands,
// code spans, fences, and link destinations retain their literal meaning.
markdownIt.inline.ruler.before('entity', 'double_escaped_entity', (state, silent) => {
  if (state.src.charCodeAt(state.pos) !== 0x26) return false;
  const match = /^&amp;(#(?:x[0-9a-f]{1,8}|[0-9]{1,8})|[a-z][a-z0-9]{1,31});/i.exec(state.src.slice(state.pos, state.posMax));
  if (!match) return false;
  // Use the parser's decoder, which validates code points and leaves invalid
  // or unknown entities literal instead of throwing during message rendering.
  if (!silent) state.pending += markdownIt.utils.unescapeAll(`&${match[1]};`);
  state.pos += match[0].length;
  return true;
});
// Imported per-file, not from 'react-syntax-highlighter/styles/hljs'. That
// barrel eagerly require()s all 80 bundled themes (~336 KB of source) at module
// scope, so naming five exports off it still pulls every one of them.
import atomOneDark from 'react-syntax-highlighter/styles/hljs/atom-one-dark';
import atomOneLight from 'react-syntax-highlighter/styles/hljs/atom-one-light';
import monokaiSublime from 'react-syntax-highlighter/styles/hljs/monokai-sublime';
import monoBlue from 'react-syntax-highlighter/styles/hljs/mono-blue';
import tomorrowNightBlue from 'react-syntax-highlighter/styles/hljs/tomorrow-night-blue';
// Resolves to a stub on web and to highlight.js on native — see hljsHighlighter.jsx.
import { highlightCodeNative } from '@/components/Messages/hljsHighlighter';
import { nativeMarkdownAST, retainNativeMarkdownAST } from './nativeMarkdownCache';

import WebCodeBlock from './WebCodeBlock';

import Icon from '@/components/Icon';
import useResponsiveness from '@/hooks/useResponsiveness';
import PropTypes from 'prop-types';
import { useModalStore, useChatStore, useChatInputStore, useToastStore } from '@/store';
import { useAuthStore } from '@/store/auth';
import { useModalHelper } from '@/hooks/useModalHelper';
import copyText from '@/constants/utils/copy_text';
import { extractMarkdownTableData, rowsToMarkdown, rowsToTsv } from '@/constants/utils/extract_markdown_table_data';
import { shouldRenderRawMessageHtml } from '@/constants/utils/rawHtmlSafety';
import { normalizeStandaloneHtml } from '@/constants/utils/htmlArtifactMarkdown';
import { parseChartSpec } from '@/constants/utils/chart_spec';
import { validateAnswer, validateStreamingAnswer, inspectAnswerBlocks, answerWasRequested, normalizeAnswerBlocks, repairAnswerBlocks } from '@/constants/utils/gabUi.cjs';
import InteractiveAnswer from './InteractiveAnswer';
import { InteractiveAnswerContext } from './InteractiveAnswerContext';
import { parseRecipeSpec } from '@/constants/utils/recipe_spec';
import { parseWorkoutSpec } from '@/constants/utils/workout_spec';
import { parsePlacesSpec } from '@/constants/utils/places_spec';
import { getActiveEditorInsert } from '@/lib/editorInsertRegistry';
import { canUseDesktopEditors, createAndOpenEditor, openMessageFile } from '@/constants/utils/files';
import { MESSAGE_LAYOUT, AGENT_URL_PREFIX } from '@/constants/config';
import { isDarkTheme } from '@/constants/Colors';
import { InlineMath, DisplayMath } from './MathRenderer';
import InlineChart from './InlineChart';
import RecipeCard from './RecipeCard';
import WorkoutCard from './WorkoutCard';
import MessagePlaces from './MessagePlaces';
import StreamingCardPlaceholder from './StreamingCardPlaceholder';

const HTML_PREVIEW_LANGUAGES = ['html', 'htm'];
const STREAMING_CARD_KINDS = ['gab-ui', 'chart', 'recipe', 'workout', 'places', ...EXPERIENCE_KINDS, ...HTML_PREVIEW_LANGUAGES];

// Style for the invisible trailing guard line appended to iOS paragraphs (see
// the paragraph rule). The \n before the zero-width space starts a new
// paragraph, and on iOS lineHeight is applied per paragraph, so the guard line
// only occupies ~2px regardless of the body's 25.5px line height.
const iosLastLineClipGuardStyle = { fontSize: 2, lineHeight: 2 };

// A non-editable multiline TextInput is backed by a real UITextView, which is
// the only way to get partial text selection on iOS: RN's <Text selectable>
// just attaches a long-press recognizer that copies the whole node. A
// UITextView swallows taps on its own text though, so a paragraph only
// qualifies if nothing inside it is interactive (links, citations).
const INTERACTIVE_PROP_NAMES = ['onPress', 'onLongPress', 'onPressIn', 'onPressOut'];

// UITextView derives its text container inset from padding, so the bottom slack
// plays the same role as iosLastLineClipGuardStyle without putting a stray
// newline into copied text.
const iosTextInputResetStyle = { padding: 0, paddingBottom: 2 };

// A read-only UITextView can report an intrinsic height short of the text it
// goes on to draw, which hides the tail of a long answer (copy still returns
// everything, since it reads the message rather than the view). <Text> measures
// wrapped text reliably, so an invisible copy of the same children lays the
// block out and the UITextView is stretched over it.
//
// Sizing from a sibling keeps this one-way on purpose: taking the height from
// the UITextView's own measurement instead feeds its frame back into the
// measurement that produced it, and the block grows a little on every pass —
// which drags the chat list's content size, and the scroll position with it.
const iosSelectableSizerStyle = { opacity: 0 };
const iosSelectableFillStyle = { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 };

// Android 15 changed how TextView derives a visual width for AT_MOST text.
// RN 0.79 can consequently measure a selectable Text at one width and paint
// it at another, allocating too few wrapped lines and clipping the tail.
// An explicit width gives Fabric an EXACTLY constraint and avoids that path.
const androidSelectableBlockStyle = { width: '100%' };

// Android and macOS <Text selectable> already support partial selection, so
// adjacent prose can share a view; iOS needs the UITextView swap as well. The
// Mac message host coordinates selection across the remaining views. `mergeable` marks
// body-level blocks — prose, headings and flattened lists — that the body rule
// may fold into a shared text view.
function NativeSelectableParagraph({ containerStyle, textStyle, children, selectable = true }) {
  if (Platform.OS === 'macos') {
    return (
      <View style={containerStyle}>
        <Text style={textStyle} selectable={selectable}>{children}</Text>
      </View>
    );
  }
  if (Platform.OS === 'ios') {
    return (
      <View style={containerStyle}>
        <Text
          style={[textStyle, iosSelectableSizerStyle]}
          accessible={false}
          importantForAccessibility="no-hide-descendants"
        >
          {children}
        </Text>
        <TextInput
          style={[textStyle, iosSelectableFillStyle]}
          editable={false}
          multiline
          scrollEnabled={false}
          dataDetectorTypes="none"
          accessibilityRole="text"
        >
          {children}
        </TextInput>
      </View>
    );
  }
  return (
    <View style={[containerStyle, androidSelectableBlockStyle]}>
      <Text
        style={[textStyle, androidSelectableBlockStyle]}
        selectable={selectable}
        textBreakStrategy="simple"
        android_hyphenationFrequency="none"
      >
        {children}
      </Text>
    </View>
  );
}

// A list item, held in a form the list rules can render either way: as a
// bullet in its own row, or — when every item in the list is plain prose —
// flattened into one line of a shared text view so a selection can be dragged
// through the list. The row layout puts each item in a view of its own, and a
// mobile selection cannot cross views. The Mac message host bridges those rows.
//
// The row deliberately holds plain text. A read-only TextInput reports no
// usable intrinsic height inside a flex slot like bullet_list_content, so the
// row collapses and the message paints over whatever follows it.
function NativeListItem({ prefix, itemStyle, iconStyle, contentStyle, selectable, children }) {
  return (
    <View style={itemStyle}>
      <Text style={iconStyle} selectable={Platform.OS === 'macos' && selectable}>{prefix}</Text>
      <View style={contentStyle}>
        {children}
      </View>
    </View>
  );
}

// list_item marginTop + marginBottom, rebuilt as a blank line once the items
// share a text view.
const nativeListItemGapStyle = { fontSize: 8, lineHeight: 8 };

// Flattening trades the hanging indent for selectability: a wrapped line sits
// under the bullet rather than under the text, because a text view has no way
// to indent continuation lines.
function flattenNativeList({ children, listStyle, baseTextStyle, lineStyle, enabled, nodeKey, selectable = true }) {
  if (!enabled) return null;

  const items = React.Children.toArray(children);
  const canFlatten = items.length > 0 && items.every(item => (
    React.isValidElement(item)
    && item.type === NativeListItem
    && isInertTextTree(item.props.children)
  ));
  if (!canFlatten) return null;

  const lines = [];
  items.forEach((item, index) => {
    // react-native-markdown-display does not always assign keys to the
    // intermediate elements it gives this rule. Include the local position so
    // multiple unkeyed list items never collapse to `gap-null` / `item-null`.
    const itemKey = item.key ?? index;
    if (index > 0) {
      // Different nested lineHeight spans crop their parent Text on Android.
      // One ordinary line break keeps every list line on the 25.5px root
      // metric. iOS can retain the compact 8px spacer.
      lines.push(Platform.OS === 'android'
        ? <Text key={`gap-${itemKey}-${index}`}>{'\n'}</Text>
        : <Text key={`gap-${itemKey}-${index}`} style={nativeListItemGapStyle}>{'\n\n'}</Text>);
    }
    lines.push(
      <Text key={`item-${itemKey}-${index}`} style={lineStyle}>
        {`${item.props.prefix}\u00A0\u00A0`}
        {item.props.children}
      </Text>
    );
  });

  return (
    <NativeSelectableParagraph
      key={nodeKey}
      mergeable
      gapBefore={listStyle?.marginTop || 0}
      gapAfter={listStyle?.marginBottom || 0}
      containerStyle={listStyle}
      textStyle={baseTextStyle}
      selectable={selectable}
    >
      {lines}
    </NativeSelectableParagraph>
  );
}

// Vertical space between two merged blocks. Margins can't do the job once the
// blocks share a text view, so it is rebuilt as a blank line of that height —
// and collapsed the way adjoining CSS margins would be.
function gapBetween(previous, next) {
  return Math.max(previous.props.gapAfter || 0, next.props.gapBefore || 0);
}

// Selection cannot cross native text view boundaries, so one text view
// per block walls the drag handles in at every block break. Runs of adjacent
// mergeable blocks are collapsed into a single text view here so a selection
// can be dragged from a heading through the paragraphs that follow it.
function mergeSelectableParagraphs(children, baseTextStyle) {
  const items = Array.isArray(children) ? children : [children];
  const output = [];
  let run = [];
  let didMerge = false;

  const flushRun = () => {
    if (run.length === 0) return;
    if (run.length === 1) {
      output.push(run[0]);
    } else {
      const combined = [];
      run.forEach((element, index) => {
        const elementKey = element.key ?? index;
        if (index > 0) {
          const gap = gapBetween(run[index - 1], element);
          const gapSize = Math.max(gap, 1);
          // Android's CustomLineHeightSpan mismeasures a parent containing
          // nested spans with different line heights. Keep the root metric
          // uniform there, but retain a blank line when the source blocks have
          // a gap. A single newline erases paragraph separation. iOS still
          // uses the precise collapsed margin.
          combined.push(Platform.OS === 'android'
            ? <Text key={`gap-${elementKey}-${index}`}>{gap > 0 ? '\n\n' : '\n'}</Text>
            : (
              <Text key={`gap-${elementKey}-${index}`} style={{ fontSize: gapSize, lineHeight: gapSize }}>
                {gap > 0 ? '\n\n' : '\n'}
              </Text>
            ));
        }
        // Each block keeps its own typography here, which is what lets a
        // heading share a text view with the prose under it.
        combined.push(
          <Text key={`block-${elementKey}-${index}`} style={element.props.textStyle}>
            {element.props.children}
          </Text>
        );
      });

      const first = run[0];
      const last = run[run.length - 1];
      output.push(
        <NativeSelectableParagraph
          // Android uses this same view from the first paragraph onward,
          // including when another paragraph/list extends the merged run.
          key={Platform.OS === 'android' ? first.key : `merged-${first.key ?? output.length}-${output.length}`}
          containerStyle={[
            first.props.containerStyle || { marginTop: first.props.gapBefore || 0 },
            { marginBottom: last.props.gapAfter || 0 },
          ]}
          textStyle={baseTextStyle}
          selectable={run.every(element => element.props.selectable !== false)}
        >
          {combined}
        </NativeSelectableParagraph>
      );
      didMerge = true;
    }
    run = [];
  };

  for (const child of items) {
    if (React.isValidElement(child) && child.type === NativeSelectableParagraph && child.props.mergeable) {
      run.push(child);
    } else {
      flushRun();
      output.push(child);
    }
  }
  flushRun();

  return didMerge ? output : children;
}

function isInertTextTree(node) {
  if (node == null || typeof node === 'boolean') return true;
  if (typeof node === 'string' || typeof node === 'number') return true;
  if (Array.isArray(node)) return node.every(isInertTextTree);
  if (!React.isValidElement(node)) return false;

  const type = node.type;
  const typeName = typeof type === 'string' ? type : (type?.displayName || type?.name || '');
  if (type !== Text && typeName !== 'Text' && typeName !== 'InlineMath') return false;
  if (INTERACTIVE_PROP_NAMES.some((prop) => node.props?.[prop])) return false;

  return React.Children.toArray(node.props?.children).every(isInertTextTree);
}

/**
 * Checks if a single line looks like code
 */
const isCodeLine = (line) => {
  if (!line.trim()) return false; // Empty lines are neutral
  
  const codePatterns = [
    /^\s*[\{\[\(]/, // Starts with brackets
    /[\{\[\(\}\]\)];?\s*$/, // Ends with brackets
    /^\s*[\}\]\)]/, // Starts with closing brackets
    /^[\s]*(const|let|var|function|import|export|class|async|await|return|if|else|for|while|try|catch)\s/,
    /=>/,  // Arrow functions
    /===|!==|&&|\|\|/, // Logical operators
    /^\s*\/\//, // JS comments
    /\.(then|catch|map|filter|forEach|some|every|find|includes)\(/, // Method chains
    /^\s*"[^"]+"\s*:/, // JSON keys
    /^[\s]*\w+:\s*[\[\{\('"0-9]/, // Key-value with value starting
    /^\s*<\/?[a-zA-Z]/, // HTML/JSX tags
  ];
  
  let matchCount = 0;
  for (const pattern of codePatterns) {
    if (pattern.test(line)) matchCount += 1;
    if (matchCount >= 2) return true;
  }
  return false;
};

/**
 * Detects the likely language of a code block
 */
const detectLanguage = (codeLines) => {
  let scores = { javascript: 0, json: 0, python: 0, html: 0, css: 0, shell: 0 };
  
  for (const line of codeLines) {
    // JSON
    if (/^\s*"[^"]+"\s*:/.test(line)) scores.json += 2;
    if (/^\s*[\{\[]$/.test(line) || /^[\}\]],?$/.test(line)) scores.json++;
    
    // JavaScript
    if (/^[\s]*(const|let|var|function|import|export|class|async|await)\s/.test(line)) scores.javascript += 2;
    if (/=>/.test(line)) scores.javascript++;
    if (/\.(then|catch|map|filter|forEach|some)\(/.test(line)) scores.javascript++;
    
    // Python
    if (/^[\s]*(def|class|import|from|elif|except|with)\s/.test(line)) scores.python += 2;
    
    // HTML
    if (/^[\s]*<\/?[a-zA-Z][^>]*>/.test(line)) scores.html += 2;
    
    // CSS
    if (/^[\s]*[\w-]+\s*:\s*[^;]+;/.test(line)) scores.css++;
    
    // Shell
    if (/^\$\s/.test(line) || /^[\s]*(sudo|npm|yarn|git)\s/.test(line)) scores.shell += 2;
  }
  
  const maxScore = Math.max(...Object.values(scores));
  if (maxScore === 0) return 'javascript';
  
  const lang = Object.entries(scores).find(([_, score]) => score === maxScore)?.[0];
  return lang === 'shell' ? 'bash' : lang;
};

/**
 * Processes user content to wrap code segments in code blocks
 * Separates plain text from code and only wraps the code portions
 */
const processUserContent = (content) => {
  if (!content || typeof content !== 'string') return content;
  
  // Already has code fences, don't process
  if (content.includes('```')) return content;
  
  const lines = content.split('\n');
  const segments = [];
  let currentSegment = { type: 'text', lines: [] };
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const looksLikeCode = isCodeLine(line);
    const isEmpty = !line.trim();
    
    if (isEmpty) {
      // Empty lines go to current segment
      currentSegment.lines.push(line);
    } else if (looksLikeCode) {
      if (currentSegment.type === 'text' && currentSegment.lines.some(l => l.trim())) {
        // Save current text segment and start code segment
        segments.push(currentSegment);
        currentSegment = { type: 'code', lines: [line] };
      } else if (currentSegment.type === 'text') {
        // Current segment is empty text, convert to code
        currentSegment.type = 'code';
        currentSegment.lines.push(line);
      } else {
        // Already in code segment
        currentSegment.lines.push(line);
      }
    } else {
      if (currentSegment.type === 'code' && currentSegment.lines.some(l => l.trim())) {
        // Save current code segment and start text segment
        segments.push(currentSegment);
        currentSegment = { type: 'text', lines: [line] };
      } else if (currentSegment.type === 'code') {
        // Current segment is empty code, convert to text
        currentSegment.type = 'text';
        currentSegment.lines.push(line);
      } else {
        // Already in text segment
        currentSegment.lines.push(line);
      }
    }
  }
  
  // Don't forget the last segment
  if (currentSegment.lines.length > 0) {
    segments.push(currentSegment);
  }
  
  // Only process if we found actual code segments with enough lines
  const hasCodeSegment = segments.some(s => s.type === 'code' && s.lines.filter(l => l.trim()).length >= 2);
  if (!hasCodeSegment) return content;
  
  // Build the result
  const result = segments.map(segment => {
    if (segment.type === 'code' && segment.lines.filter(l => l.trim()).length >= 2) {
      const lang = detectLanguage(segment.lines);
      // Trim leading/trailing empty lines from code block
      const trimmedLines = segment.lines.join('\n').trim();
      return '```' + lang + '\n' + trimmedLines + '\n```';
    }
    return segment.lines.join('\n');
  }).join('\n');
  
  return result;
};

/**
 * Checks if content contains LaTeX math delimiters or MathML tags.
 * For single $, requires a letter/backslash after it to avoid matching currency.
 */
const hasMathDelimiters = (content) => {
  if (!content) return false;
  return /\\\(|\\\[|\$\$|\$[a-zA-Z\\{(]|<math[\s>]/i.test(content);
};

/**
 * Extracts math expressions from content and replaces them with renderable placeholders.
 * - Display math (\[...\], $$...$$) → fenced code block with 'math' language
 * - MathML display (<math display="block">...</math>) → fenced code block with 'math' language
 * - Inline math (\(...\), $...$) → %%MI{index}%% placeholder
 * - MathML inline (<math>...</math>) → %%MI{index}%% placeholder
 * Code blocks and inline code are preserved (not processed for math).
 */
const extractMathExpressions = (content) => {
  if (!content || typeof content !== 'string' || !hasMathDelimiters(content)) {
    return { processed: content, inlineExpressions: [] };
  }

  const inlineExpressions = [];
  let processed = content;

  // Protect existing code blocks and inline code from math extraction
  const codeBlocks = [];
  processed = processed.replace(/(```[\s\S]*?```|`[^`\n]+`)/g, (match) => {
    const idx = codeBlocks.length;
    codeBlocks.push(match);
    return `%%CB${idx}%%`;
  });

  // MathML display: <math display="block">...</math> → math code fence
  processed = processed.replace(/<math\s+display\s*=\s*"block"[\s\S]*?<\/math>/gi, (match) => {
    return '\n\n```math\n' + match.trim() + '\n```\n\n';
  });

  // MathML inline: <math>...</math> or <math display="inline">...</math> → placeholder
  processed = processed.replace(/<math(?:\s+display\s*=\s*"inline")?[\s>][\s\S]*?<\/math>/gi, (match) => {
    const idx = inlineExpressions.length;
    inlineExpressions.push(match.trim());
    return `%%MI${idx}%%`;
  });

  // Display math: $$...$$ → math code fence (must run before single $ extraction)
  processed = processed.replace(/\$\$([\s\S]*?)\$\$/g, (_, expr) => {
    return '\n\n```math\n' + expr.trim() + '\n```\n\n';
  });

  // Display math: \[...\] → math code fence
  processed = processed.replace(/\\\[([\s\S]*?)\\\]/g, (_, expr) => {
    return '\n\n```math\n' + expr.trim() + '\n```\n\n';
  });

  // Inline math: \(...\) → placeholder
  processed = processed.replace(/\\\((.+?)\\\)/g, (_, expr) => {
    const idx = inlineExpressions.length;
    inlineExpressions.push(expr.trim());
    return `%%MI${idx}%%`;
  });

  // Inline math: $...$ → placeholder (single dollar, no newlines inside).
  // Requires non-space after opening $ and before closing $ to avoid currency false positives.
  processed = processed.replace(/(?<![\\$])\$(\S(?:[^\$\n]*?\S)?)\$(?!\d)/g, (_, expr) => {
    const idx = inlineExpressions.length;
    inlineExpressions.push(expr.trim());
    return `%%MI${idx}%%`;
  });

  // Restore code blocks
  processed = processed.replace(/%%CB(\d+)%%/g, (_, idx) => codeBlocks[parseInt(idx)]);

  return { processed, inlineExpressions };
};

const FONT_FAMILY = Platform.select({
  ios: 'System',
  macos: 'System',
  android: 'Roboto',
  default: 'ui-sans-serif,-apple-system, system-ui, -apple-system, BlinkMacSystemFont, Roboto, Ubuntu, "Open Sans", Arial, "Helvetica Neue", Segoe UI Emoji, Segoe UI Symbol, sans-serif'
});

const getMessageTextColor = (isDestructive, messageLayout, isUser, colors) => {
  if (isDestructive) return colors.color_destructive;
  if (isUser) return colors.text_color_user_bubble || colors.text_color_primary;
  return colors.text_color_primary;
}

/**
 * Horizontally scrollable table container with fade indicators.
 * On web, uses a native <div> with overflow-x:auto because RN Web's
 * horizontal ScrollView uses flex-row internally and won't constrain
 * to parent width. On native, uses ScrollView which works correctly.
 */
const TableColumnWidths = React.createContext([]);
const TableCell = ({ children, style, columnIndex }) => {
  const width = useContext(TableColumnWidths)[columnIndex];
  return <View style={[style, width && { width, flexBasis: width, flexGrow: 0, flexShrink: 0, minWidth: 0 }]}>{children}</View>;
};

const ScrollableTable = React.memo(({ children, tableStyle, fadeColor, rows, fontSize }) => {
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [containerWidth, setContainerWidth] = useState(0);
  const scrollRef = useRef(null);
  const widths = useMemo(() => tableColumnWidths(rows, containerWidth, fontSize), [rows, containerWidth, fontSize]);
  const sizedTableStyle = [tableStyle, { width: widths.reduce((sum, width) => sum + width, 0) }];
  const cells = <TableColumnWidths.Provider value={widths}>{children}</TableColumnWidths.Provider>;

  const handleContainerLayout = useCallback((e) => {
    setContainerWidth(e.nativeEvent.layout.width);
  }, []);

  const checkScroll = useCallback(() => {
    if (Platform.OS === 'web') {
      const el = scrollRef.current;
      if (!el) return;
      const { scrollLeft, scrollWidth, clientWidth } = el;
      setContainerWidth(clientWidth);
      const overflows = scrollWidth > clientWidth + 2;
      setCanScrollLeft(overflows && scrollLeft > 5);
      setCanScrollRight(overflows && scrollLeft < scrollWidth - clientWidth - 5);
    }
  }, []);

  // Native scroll handler
  const handleScroll = useCallback((e) => {
    if (Platform.OS !== 'web') {
      const { contentOffset, contentSize, layoutMeasurement } = e.nativeEvent;
      const overflows = contentSize.width > layoutMeasurement.width + 2;
      setCanScrollLeft(overflows && contentOffset.x > 5);
      setCanScrollRight(overflows && contentOffset.x < contentSize.width - layoutMeasurement.width - 5);
    }
  }, []);

  // Web: attach scroll + resize listeners
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const el = scrollRef.current;
    if (!el) return;

    checkScroll();
    el.addEventListener('scroll', checkScroll, { passive: true });

    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(checkScroll);
      ro.observe(el);
    }

    return () => {
      el.removeEventListener('scroll', checkScroll);
      ro?.disconnect();
    };
  }, [checkScroll]);

  useEffect(() => { checkScroll(); }, [checkScroll, rows, containerWidth]);

  const fadeLeft = canScrollLeft ? (
    <View style={scrollFadeStyles.fadeLeft} pointerEvents="none">
      <LinearGradient
        colors={[fadeColor, 'transparent']}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 0 }}
        style={StyleSheet.absoluteFill}
      />
    </View>
  ) : null;

  const fadeRight = canScrollRight ? (
    <View style={scrollFadeStyles.fadeRight} pointerEvents="none">
      <LinearGradient
        colors={['transparent', fadeColor]}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 0 }}
        style={StyleSheet.absoluteFill}
      />
    </View>
  ) : null;

  const scrollHint = canScrollRight && !canScrollLeft ? (
    <View style={scrollFadeStyles.hintContainer} pointerEvents="none">
      <Text style={scrollFadeStyles.hintText}>Scroll →</Text>
    </View>
  ) : null;

  if (Platform.OS === 'web') {
    return (
      <div style={{ position: 'relative' }}>
        <style dangerouslySetInnerHTML={{ __html: `
          [data-table-scroll] {
            overflow-x: scroll !important;
            overflow-y: hidden !important;
          }
          [data-table-scroll]::-webkit-scrollbar {
            height: 8px;
            display: block !important;
          }
          [data-table-scroll]::-webkit-scrollbar-track {
            background: rgba(128, 128, 128, 0.1);
            border-radius: 4px;
          }
          [data-table-scroll]::-webkit-scrollbar-thumb {
            background: rgba(128, 128, 128, 0.4);
            border-radius: 4px;
            min-width: 40px;
          }
          [data-table-scroll]::-webkit-scrollbar-thumb:hover {
            background: rgba(128, 128, 128, 0.65);
          }
        `}} />
        <div
          ref={scrollRef}
          data-table-scroll=""
          style={{
            WebkitOverflowScrolling: 'touch',
            paddingBottom: 2,
          }}
        >
          <View style={sizedTableStyle}>
            {cells}
          </View>
        </div>
        {fadeLeft}
        {fadeRight}
        {scrollHint}
      </div>
    );
  }

  return (
    <View style={{ position: 'relative' }} onLayout={handleContainerLayout}>
      <ScrollView
        ref={scrollRef}
        horizontal
        showsHorizontalScrollIndicator={Platform.OS === 'macos'}
        onContentSizeChange={Platform.OS === 'macos' ? width => setCanScrollRight(width > containerWidth + 2) : undefined}
        onScroll={handleScroll}
        scrollEventThrottle={16}
      >
        <View style={sizedTableStyle}>
          {cells}
        </View>
      </ScrollView>
      {Platform.OS !== 'macos' && fadeLeft}
      {Platform.OS !== 'macos' && fadeRight}
      {scrollHint}
    </View>
  );
});

const scrollFadeStyles = StyleSheet.create({
  fadeLeft: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: 28,
    zIndex: 1,
  },
  fadeRight: {
    position: 'absolute',
    right: 0,
    top: 0,
    bottom: 0,
    width: 28,
    zIndex: 1,
  },
  hintContainer: {
    position: 'absolute',
    right: 8,
    bottom: 8,
    backgroundColor: 'rgba(0,0,0,0.55)',
    borderRadius: 4,
    paddingHorizontal: 8,
    paddingVertical: 3,
    zIndex: 2,
  },
  hintText: {
    color: '#fff',
    fontSize: 11,
    fontWeight: '600',
  },
});

const createWrapperStyles = (colors) => StyleSheet.create({
  container: {
    borderRadius: 8,
    marginTop: Platform.OS === 'macos' ? 14 : 8,
    marginBottom: Platform.OS === 'macos' ? 14 : 8,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    overflow: 'hidden',
    width: '100%',
    ...(Platform.OS === 'web' ? { maxWidth: '100%', flexShrink: 1, boxSizing: 'border-box' } : { flexShrink: 0 }),
    minWidth: 0,
  },
  header: {
    paddingLeft: 16,
    paddingRight: 16,
    paddingTop: 6,
    paddingBottom: 6,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    ...(Platform.OS === 'macos' && {
      paddingVertical: 8,
      borderBottomWidth: 1,
      borderBottomColor: colors.border_color_secondary,
    }),
  },
  headerActions: {
    flexDirection: 'row',
    gap: 4,
  },
  headerText: {
    color: colors.text_color_secondary,
    fontSize: 12,
    textTransform: 'capitalize',
  },
  icon: {
    marginRight: 4,
  },
  button: {
    paddingLeft: 8,
    paddingRight: 8,
    paddingTop: 4,
    paddingBottom: 4,
    borderRadius: 4,
    flexDirection: 'row',
    alignItems: 'center',
  },
});

// Recursively extract styled text segments from a markdown AST node.
// Returns an array of { text, bold, italic, code, strikethrough } objects,
// or null if the content contains math/citations that need special rendering.
function extractTextSegments(node, inherited = {}) {
  if (!node) return null;

  // Leaf text node
  if (node.type === 'text') {
    if (node.content.includes('%%MI') || /\[\d+\]/.test(node.content)) {
      return null;
    }
    return [{ text: node.content, ...inherited }];
  }

  // Line breaks
  if (node.type === 'softbreak' || node.type === 'hardbreak') {
    return [{ text: '\n', ...inherited }];
  }

  // Inline code
  if (node.type === 'code_inline') {
    if (node.content.includes('%%MI') || /\[\d+\]/.test(node.content)) {
      return null;
    }
    return [{ text: node.content, ...inherited, code: true }];
  }

  // Links or images — fall back to regular rendering
  if (node.type === 'link' || node.type === 'image') {
    return null;
  }

  // Container nodes: propagate style
  if (node.children && node.children.length > 0) {
    const childInherited = { ...inherited };
    if (node.type === 'strong') childInherited.bold = true;
    if (node.type === 'em') childInherited.italic = true;
    if (node.type === 's') childInherited.strikethrough = true;

    const segments = [];
    for (const child of node.children) {
      const childSegments = extractTextSegments(child, childInherited);
      if (childSegments === null) return null;
      segments.push(...childSegments);
    }
    return segments;
  }

  // Unknown node type with no children — skip
  return [];
}

// Error boundary for markdown rendering — catches crashes (e.g. Android nesting
// violations) and falls back to plain text so the app stays alive.
class MarkdownErrorBoundary extends React.Component {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    console.warn('[MarkdownErrorBoundary] Render error caught:', error?.message);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback || null;
    }
    return this.props.children;
  }
}

// Keep the prose cursor's timer outside the message renderer. Card placeholders
// have their own quiet animation and don't need a second indicator underneath.
function StreamingMessageCursor({ style, selectionActiveRef }) {
  const [index, setIndex] = useState(0);
  const frames = '⠋⠙⠹⠸⠼⠴⠦⠴⠦⠖⠒⠐⠐⠒⠓⠋';
  useEffect(() => {
    const timer = setInterval(() => {
      if (!selectionActiveRef.current) setIndex(previous => (previous + 1) % frames.length);
    }, 400);
    return () => clearInterval(timer);
  }, [selectionActiveRef]);
  return <Text style={style} accessible={false} dataSet={{ messageStreamingCursor: '' }}>{frames[index]}</Text>;
}

const containsStreamingCard = nodes => nodes.some(node => (
  (node.type === 'fence' && STREAMING_CARD_KINDS.includes(node.sourceInfo?.trim().toLowerCase())) ||
  containsStreamingCard(node.children || [])
));

// Memoized markdown content to avoid re-rendering which can clear selection on web
const MarkdownContent = React.memo(function MarkdownContent({ markdownStyles, markdownRules, content, isStreaming, cardPlaceholdersEnabled, streamingCursor }) {
  // Android must keep the same structural keys when streaming finishes.
  // Switching back to a string lets the library assign fresh keys and remount
  // every native text/code view. Reuse the AST across that transition too.
  const usesAST = Platform.OS === 'macos' || Platform.OS === 'android' || isStreaming || /```gab-ui\b/i.test(content);
  const parsed = useMemo(() => usesAST
    ? nativeMarkdownAST(content, markdownIt, false) : content, [content, usesAST]);
  const hasStreamingCard = isStreaming && cardPlaceholdersEnabled && containsStreamingCard(parsed);
  useEffect(() => {
    if ((Platform.OS === 'macos' || Platform.OS === 'android') && !isStreaming) retainNativeMarkdownAST(content, markdownIt, parsed);
  }, [content, parsed, isStreaming]);
  return (
    <>
      <Markdown
        style={markdownStyles}
        mergeStyle={false}
        rules={markdownRules}
        markdownit={markdownIt}
      >
        {parsed}
      </Markdown>
      {!hasStreamingCard && streamingCursor}
    </>
  );
});

function MessageContentRenderer({
  content: propContent,
  theme: { colors, theme },
  isStreaming,
  messageLayout,
  isUser,
  isError,
  isDeleted,
  messageId,
  conversationId,
}) {
  const { openModal } = useModalStore(state => state.actions);
  const { addToast } = useToastStore(state => state.actions);
  const { useConfirmationModal } = useModalHelper();
  const showConfirmationModal = useConfirmationModal();
  const setSelectedCitation = useChatStore(state => state.setSelectedCitation);
  const selectedCitation = useChatStore(state => state.selectedCitation);
  const isFromTemporaryChat = useChatStore(state => Boolean(findChatById(state.chats, conversationId)?.flags?.isTemporary));
  const isWorkSession = useChatStore(state => {
    const chat = findChatById(state.chats, conversationId);
    return Boolean(chat?.coworkerSession || chat?.flags?.isCoworkerSession);
  });
  const storedAnswerAllowed = useChatStore(state => answerWasRequested(findChatById(state.chats, conversationId)?.messages, messageId, true));
  const projectedAnswer = useContext(InteractiveAnswerContext);
  const interactiveAnswerAllowed = projectedAnswer && projectedAnswer.conversationId === conversationId && projectedAnswer.messageId === messageId
    ? projectedAnswer.allowed === true : storedAnswerAllowed;
  const interactiveAnswerOwner = useAuthStore(state => state.user?.id);
  const compactWorkText = Platform.OS !== 'macos' && isWorkSession;
  const projectSourceReference = useProjectSourceReference(conversationId, !isUser && !isError && !isDeleted);
  const workArtifactReference = useWorkArtifactReference(conversationId, !isUser && !isError && !isDeleted);
  const setQuote = useChatInputStore(state => state.setQuote);
  const [copiedStates, setCopiedStates] = React.useState({});
  const { isXS, showsScrollIndicator } = useResponsiveness();
  const fontScale = useMacFontScale();
  const wrapperStyles = useMemo(() => scaleTextStyles(createWrapperStyles(colors), fontScale), [colors, fontScale]);
  
  // Web-only selection popup state (imperative DOM, no React state)
  const containerRef = useRef(null);
  const popupDomRef = useRef(null);
  const selectedTextRef = useRef('');
  const conversationIdRef = useRef(conversationId);
  conversationIdRef.current = conversationId;
  const sideChat = useContext(SideChatContext);
  const sideChatRef = useRef(null);
  sideChatRef.current = !isStreaming && messageId && conversationId
    ? { ...sideChat, messageId, conversationId } : null;
  const selectionActiveRef = useRef(false);
  const selectionTimerRef = useRef(null);
  const lastSelectionTextRef = useRef('');
  const lastPosRef = useRef({ top: 0, left: 0 });
  const mathExpressionsRef = useRef([]);

  // Helper: show/hide/update web popup without React state
  const showPopupAt = useCallback((top, left) => {
    if (Platform.OS !== 'web') return;
    if (typeof document === 'undefined') return;
    if (isUser || isError || isDeleted) return; // Only show for valid, non-user messages

    if (!popupDomRef.current) {
      const el = document.createElement('div');
      el.setAttribute('role', 'toolbar');
      el.setAttribute('tabindex', '-1');
      // Base styles
      el.style.position = 'fixed';
      el.style.zIndex = '2147483647';
      el.style.border = `1px solid ${colors.border_color_input}`;
      el.style.borderRadius = '8px';
      el.style.boxShadow = '0 10px 28px rgba(0,0,0,0.45)';
      el.style.userSelect = 'none';
      el.style.whiteSpace = 'nowrap';
      el.style.fontSize = '14px';
      el.style.fontWeight = '600';
      el.style.fontFamily = 'system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif';
      el.style.background = colors.solid_color_primary;
      el.style.color = getMessageTextColor(false, messageLayout, isUser, colors);
      el.style.display = 'flex';
      el.style.flexDirection = 'row';
      el.style.alignItems = 'center';
      el.style.justifyContent = 'center';
      el.style.boxSizing = 'border-box';
      el.style.height = '36px';
      el.style.overflow = 'hidden';
      // Quote in the current composer, branch beside it, or insert into an editor.
      el.innerHTML = `
        <div data-role="ask" role="button" style="display:flex;flex-direction:row;align-items:center;gap:8px;height:100%;padding:0 12px;cursor:pointer;color:inherit;">
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 16 16" aria-hidden="true" focusable="false" style="display:block;color:inherit;">
            <path fill="currentColor" d="M1,12.0053 C2.04176,11.4565 2.84966,10.8021 3.42369,10.0422 C4.01898,9.26121 4.35915,8.34301 4.44419,7.2876 C4.21033,7.37203 3.9552,7.41425 3.67882,7.41425 C2.9347,7.41425 2.31815,7.18206 1.82916,6.71768 C1.34017,6.23219 1.09567,5.6095 1.09567,4.8496 C1.09567,3.98417 1.3508,3.29815 1.86105,2.79156 C2.3713,2.26385 3.01974,2 3.80638,2 C4.82688,2 5.63478,2.40106 6.23007,3.20317 C6.82536,3.98417 7.12301,5.07124 7.12301,6.46438 C7.12301,8.11082 6.68717,9.57784 5.81549,10.8654 C4.96507,12.153 3.71071,13.1979 2.05239,14 L1,12.0053 Z M8.87699,12.0053 C9.91875,11.4565 10.7267,10.8021 11.3007,10.0422 C11.896,9.26121 12.2361,8.34301 12.3212,7.2876 C12.0873,7.37203 11.8322,7.41425 11.5558,7.41425 C10.8117,7.41425 10.1951,7.18206 9.70615,6.71768 C9.21716,6.23219 8.97267,5.6095 8.97267,4.8496 C8.97267,3.98417 9.22779,3.29815 9.73804,2.79156 C10.2483,2.26385 10.8967,2 11.6834,2 C12.7039,2 13.5118,2.40106 14.1071,3.20317 C14.7024,3.98417 15,5.07124 15,6.46438 C15,8.11082 14.5642,9.57784 13.6925,10.8654 C12.8421,12.153 11.5877,13.1979 9.92938,14 L8.87699,12.0053 Z"/>
          </svg>
          <span style="display:block;color:inherit;">Add to this chat</span>
        </div>
        <div data-role="side-chat-divider" style="display:none;width:1px;height:20px;background:currentColor;opacity:0.25;"></div>
        <button type="button" data-role="side-chat" title="Start a separate conversation with context through this message" style="display:none;align-items:center;height:100%;padding:0 12px;cursor:pointer;color:inherit;background:transparent;border:0;font:inherit;">Ask in side chat</button>
        <div data-role="speak-divider" style="width:1px;height:20px;background:currentColor;opacity:0.25;"></div>
        <div data-role="speak" role="button" style="display:flex;flex-direction:row;align-items:center;gap:8px;height:100%;padding:0 12px;cursor:pointer;color:inherit;">
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 16 16" aria-hidden="true" focusable="false" style="display:block;color:inherit;">
            <path fill="currentColor" d="M2 6.25h2.2L7.5 3.4a.75.75 0 0 1 1.24.57v8.06a.75.75 0 0 1-1.24.57L4.2 9.75H2A1.5 1.5 0 0 1 .5 8.25v-.5A1.5 1.5 0 0 1 2 6.25Zm9.14-1.86a.75.75 0 0 1 1.06 0 5.1 5.1 0 0 1 0 7.22.75.75 0 1 1-1.06-1.06 3.6 3.6 0 0 0 0-5.1.75.75 0 0 1 0-1.06Z"/>
          </svg>
          <span style="display:block;color:inherit;">Speak</span>
        </div>
        <div data-role="add-divider" style="display:none;width:1px;height:20px;background:currentColor;opacity:0.25;"></div>
        <div data-role="add" role="button" style="display:none;flex-direction:row;align-items:center;gap:8px;height:100%;padding:0 12px;cursor:pointer;color:inherit;">
          <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 16 16" aria-hidden="true" focusable="false" style="display:block;color:inherit;">
            <path fill="currentColor" d="M8 2a.75.75 0 0 1 .75.75v4.5h4.5a.75.75 0 0 1 0 1.5h-4.5v4.5a.75.75 0 0 1-1.5 0v-4.5h-4.5a.75.75 0 0 1 0-1.5h4.5v-4.5A.75.75 0 0 1 8 2Z"/>
          </svg>
          <span data-role="add-label" style="display:block;color:inherit;">Add to document</span>
        </div>
      `;

      const swallow = (e) => { if (e && e.preventDefault) e.preventDefault(); if (e && e.stopPropagation) e.stopPropagation(); };
      el.addEventListener('pointerdown', swallow);
      el.addEventListener('mousedown', swallow);
      el.addEventListener('mouseup', swallow);
      el.addEventListener('touchstart', swallow, { passive: false });
      el.addEventListener('touchend', swallow, { passive: false });
      el.addEventListener('click', (e) => {
        swallow(e);
        const target = e.target instanceof Element ? e.target : null;
        if (target?.closest('[data-role="side-chat"]')) {
          const current = sideChatRef.current;
          if (current?.ask && !current.busy && selectedTextRef.current) {
            void current.ask({ conversationId: current.conversationId, messageId: current.messageId, text: selectedTextRef.current });
            window.getSelection()?.removeAllRanges();
            el.style.display = 'none';
            selectionActiveRef.current = false;
          }
          return;
        }
        if (target?.closest('[data-role="speak"]')) {
          if (selectedTextRef.current) {
            openModal('MessageSpeakModal', { text: selectedTextRef.current });
            window.getSelection()?.removeAllRanges();
            el.style.display = 'none';
            selectionActiveRef.current = false;
          }
          return;
        }
        if (target?.closest('[data-role="add"]')) {
          // Send the highlighted text into the open editor.
          const inserter = getActiveEditorInsert();
          if (inserter && selectedTextRef.current) {
            try {
              inserter.insert(selectedTextRef.current);
              window.getSelection()?.removeAllRanges();
            } catch (error) {
              console.log('---MESSAGECONTENTRENDERER...RENDER Error inserting into editor:', error);
            }
            el.style.display = 'none';
            selectionActiveRef.current = false;
          }
          return;
        }
        if (selectedTextRef.current) {
          try {
            // Multiple composers can be mounted; target this message's chat.
            if (conversationIdRef.current) useChatInputStore.getState().setContext(conversationIdRef.current);
            setQuote(selectedTextRef.current);
            window.getSelection()?.removeAllRanges();
            el.style.display = 'none';
            selectionActiveRef.current = false;
          } catch (error) {
            console.log('---MESSAGECONTENTRENDERER...RENDER Error setting quote:', error);
          }
        }
      });

      document.body.appendChild(el);
      popupDomRef.current = el;
    }

    // Update theme-dependent styles each time to keep in sync
    const el = popupDomRef.current;
    el.style.background = colors.solid_color_primary;
    el.style.border = `1px solid ${colors.border_color_input}`;
    el.style.color = getMessageTextColor(false, messageLayout, isUser, colors);

    const sideChatEl = el.querySelector('[data-role="side-chat"]');
    const sideChatDivider = el.querySelector('[data-role="side-chat-divider"]');
    const canAskInSideChat = !!sideChatRef.current?.ask;
    sideChatEl.style.display = canAskInSideChat ? 'flex' : 'none';
    sideChatEl.disabled = !!sideChatRef.current?.busy;
    sideChatDivider.style.display = canAskInSideChat ? 'block' : 'none';

    // Offer "Add to <editor>" only while an editor sidebar is open.
    const inserter = getActiveEditorInsert();
    const addEl = el.querySelector('[data-role="add"]');
    const addDividerEl = el.querySelector('[data-role="add-divider"]');
    const addLabelEl = el.querySelector('[data-role="add-label"]');
    if (inserter && addEl && addLabelEl) {
      addLabelEl.textContent = `Add to ${inserter.noun}`;
      addEl.style.display = 'flex';
      if (addDividerEl) addDividerEl.style.display = 'block';
    } else if (addEl) {
      addEl.style.display = 'none';
      if (addDividerEl) addDividerEl.style.display = 'none';
    }

    el.style.top = `${Math.max(8, top)}px`;
    el.style.left = `${Math.max(8, left)}px`;
    el.style.display = 'flex';

    // Re-clamp horizontally now that the popup width is known (it grows when
    // the "Add to …" segment is visible).
    const width = el.offsetWidth || 140;
    const maxLeft = Math.max(8, (window.innerWidth || 0) - width - 8);
    if (left > maxLeft) el.style.left = `${maxLeft}px`;
  }, [colors, messageLayout, isUser, isError, isDeleted, openModal, setQuote]);

  const hidePopup = useCallback(() => {
    if (Platform.OS !== 'web') return;
    const el = popupDomRef.current;
    if (el) el.style.display = 'none';
  }, []);

  useEffect(() => {
    return () => {
      // Cleanup popup DOM on unmount
      if (popupDomRef.current && typeof document !== 'undefined') {
        try { document.body.removeChild(popupDomRef.current); } catch {}
      }
      popupDomRef.current = null;
    };
  }, []);

  // Hide popup immediately when these flags are active
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    if (isUser || isError || isDeleted) hidePopup();
  }, [isUser, isError, isDeleted, hidePopup]);

  // Web-only: track selection after it completes (mouseup/keyup), position with viewport-fixed coords
  useEffect(() => {
    if (Platform.OS !== 'web') return;

    const processSelection = () => {
      try {
        if (isUser || isError || isDeleted) { // Only show for valid, non-user messages
          hidePopup();
          selectionActiveRef.current = false;
          return;
        }
        const selection = window.getSelection();
        const containerEl = containerRef.current;
        if (!selection || !containerEl) {
          hidePopup();
          selectionActiveRef.current = false;
          return;
        }

        if (selection.isCollapsed || !selection.rangeCount) {
          hidePopup();
          selectionActiveRef.current = false;
          return;
        }

        const anchorNode = selection.anchorNode;
        const focusNode = selection.focusNode;
        if (!anchorNode || !focusNode || !containerEl.contains(anchorNode) || !containerEl.contains(focusNode)) {
          hidePopup();
          selectionActiveRef.current = false;
          return;
        }

        const range = selection.getRangeAt(0);
        const rect = range.getBoundingClientRect();
        if (!rect || (rect.width === 0 && rect.height === 0)) {
          hidePopup();
          selectionActiveRef.current = false;
          return;
        }

        const rawText = selection.toString();
        const text = (rawText || '').trim();
        if (!text) {
          hidePopup();
          selectionActiveRef.current = false;
          return;
        }

        // Use viewport coordinates (fixed positioning)
        let top = rect.top - 40; // above selection
        let left = rect.left;

        // Clamp within viewport bounds (assume popup ~140px wide)
        const maxLeft = Math.max(0, (window.innerWidth || 0) - 140);
        top = Math.max(8, top);
        left = Math.max(8, Math.min(left, maxLeft));

        if (text !== lastSelectionTextRef.current) {
          lastSelectionTextRef.current = text;
          selectedTextRef.current = text;
        }

        if (Math.abs((lastPosRef.current.top || 0) - top) > 1 || Math.abs((lastPosRef.current.left || 0) - left) > 1) {
          lastPosRef.current = { top, left };
        }

        selectionActiveRef.current = true;
        showPopupAt(top, left);
      } catch (e) {
        // ignore
      }
    };

    const scheduleProcess = () => {
      if (selectionTimerRef.current) clearTimeout(selectionTimerRef.current);
      selectionTimerRef.current = setTimeout(processSelection, 80);
    };

    const handleKeyUp = () => scheduleProcess();
    const handleMouseUp = () => scheduleProcess();

    const handleScrollOrResize = () => {
      if (selectionActiveRef.current) scheduleProcess();
    };

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        hidePopup();
        selectionActiveRef.current = false;
      }
    };

    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('keyup', handleKeyUp);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);

    return () => {
      if (selectionTimerRef.current) {
        clearTimeout(selectionTimerRef.current);
        selectionTimerRef.current = null;
      }
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('keyup', handleKeyUp);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [hidePopup, showPopupAt, isUser, isError, isDeleted]);

  // Swallow interactions on popup so selection doesn't clear
  const swallowEvent = useCallback((e) => {
    if (!e) return;
    if (e.preventDefault) e.preventDefault();
    if (e.stopPropagation) e.stopPropagation();
  }, []);

  // Don't append spinner to content - it causes full markdown re-parse on every frame
  // The spinner will be rendered separately after the markdown content
  // For user messages, auto-detect and wrap code segments in code blocks
  // For all messages, extract LaTeX math expressions and replace with renderable placeholders
  const readableAnswerRef = useRef(null);
  const { content, mathExpressions } = useMemo(() => {
    let raw = propContent || '';
    if (interactiveAnswerAllowed && !isUser && !isError && !isDeleted) raw = repairAnswerBlocks(raw);
    const scope = JSON.stringify([interactiveAnswerOwner, conversationId, messageId, interactiveAnswerAllowed, isUser, isError, isDeleted]);
    if (readableAnswerRef.current?.scope !== scope || !isStreaming) readableAnswerRef.current = null;
    if (isStreaming && interactiveAnswerAllowed && !isUser && !isError && !isDeleted) {
      const blocks = inspectAnswerBlocks(raw.replace(/\n[ \t]*`{1,2}[ \t]*$/, ''), true);
      const previous = readableAnswerRef.current;
      if (blocks.length === 1 && blocks[0].spec && (!previous || raw.startsWith(previous.content) || !blocks[0].pending)) {
        readableAnswerRef.current = { scope, content: raw };
      } else if (previous) {
        // A bounded server repair can replace the message with a partial
        // payload. Keep its validated reading surface until the replacement
        // validates; a stopped/finished stream always uses the actual result.
        raw = previous.content;
      }
    }
    
    // Only auto-wrap for user messages
    if (isUser && raw) {
      raw = processUserContent(raw);
    } else if (!isError && !isDeleted) {
      raw = normalizeStandaloneHtml(raw, { isStreaming });
    }
    
    // Extract math expressions and replace with renderable placeholders
    const { processed, inlineExpressions } = extractMathExpressions(raw);
    
    // Trim trailing whitespace/newlines to prevent empty paragraphs and breaks
    // that render as large blank spaces (especially visible on Samsung devices)
    const trimmed = processed.trimEnd();
    
    return { content: trimmed, mathExpressions: inlineExpressions };
  }, [propContent, isUser, isError, isDeleted, isStreaming, conversationId, messageId, interactiveAnswerAllowed, interactiveAnswerOwner]);

  // Store inline math expressions in a ref for access by markdown rules (avoids re-creating rules)
  mathExpressionsRef.current = mathExpressions;

  // Memoize code background color and theme
  const codeBgColor = useMemo(() => 
    theme === 'black' ? 'rgb(11,11,11)' :
    messageLayout === MESSAGE_LAYOUT.BUBBLE ? colors.solid_color_primary :
    colors.solid_color_block,
    [theme, messageLayout, colors]
  );

  const codeTheme = useMemo(() => 
    theme === 'black' ?  atomOneDark :
    theme === 'sepia' ? monokaiSublime :
    theme === 'ocean' ? monoBlue :
    theme === 'pastel' ? atomOneLight :
    theme === 'mint' ? atomOneLight :
    theme === 'muted' ? atomOneDark :
    theme === 'night' ? tomorrowNightBlue :
    theme === 'smoke' ? atomOneDark :
    atomOneLight,
    [theme]
  );

  // Memoize markdown styles
  const markdownStyles = useMemo(() => {
    const shared = {
    // Block level elements
    body: {
      fontSize: 17,
      lineHeight: Platform.OS === 'web' ? '1.5' : 25.5,
      flexShrink: Platform.OS === 'web' ? 1 : 0,
      ...(Platform.OS === 'web' && {
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        maxWidth: '100%',
        userSelect: 'text',
      }),
    },
    paragraph: {
      fontSize: 17,
      lineHeight: Platform.OS === 'web' ? '1.5' : 25.5,
      flexShrink: Platform.OS === 'web' ? 1 : 0,
      ...(Platform.OS === 'web' && {
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        maxWidth: '100%',
        userSelect: 'text',
      }),
    },
    paragraphFirst: {
      marginTop: 0,
    },
    paragraphNotFirst: {
      marginTop: !isWorkSession && !isUser && !isError && !isDeleted ? 16 : 10,
    },
    text: {
      fontFamily: FONT_FAMILY,
      fontSize: 17,
      lineHeight: Platform.OS === 'web' ? '1.5' : 25.5,
      color: getMessageTextColor((isError || isDeleted), messageLayout, isUser, colors),
      ...(Platform.OS === 'web' && {
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        userSelect: 'text',
      }),
    },
    // Enable native text selection on Android/iOS
    selectableText: {
      fontFamily: FONT_FAMILY,
      fontSize: 17,
      lineHeight: Platform.OS === 'web' ? '1.5' : 25.5,
      color: getMessageTextColor((isError || isDeleted), messageLayout, isUser, colors),
      ...(Platform.OS === 'web' && {
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
      }),
    },
    hr: {
      backgroundColor: colors.border_color_secondary,
      height: 1,
      marginTop: 16,
      marginBottom: 16,
    },
    blockquote: {
      borderLeftColor: colors.solid_color_secondary,
      // backgroundColor: colors.solid_color_primary,
      paddingLeft: 12,
      paddingRight: 12,
      paddingTop: 8,
      paddingBottom: 8,
      borderLeftWidth: 4,
      marginTop: 8,
      marginBottom: 8,
    },
    heading1: {
      color: colors.text_color_primary,
      fontSize: isXS ? 26 : 30,
      letterSpacing: Platform.OS === 'web' ? '-.04rem' : -0.5,
      fontWeight: "600",
      lineHeight: Platform.OS === 'web' ? '1.1111111' : (isXS ? 26 : 31),
      marginBottom: 10,
      marginTop: 10,
    },
    heading2: {
      color: colors.text_color_primary,
      fontSize: isXS ? 22 : 26,
      fontWeight: "600",
      lineHeight: Platform.OS === 'web' ? '1.3333333' : (isXS ? 28 : 32),
      marginBottom: 10,
      marginTop: 20,
    },
    heading3: {
      color: colors.text_color_primary,
      fontSize: isXS ? 20 : 22,
      fontWeight: "600",
      lineHeight: Platform.OS === 'web' ? '1.6' : (isXS ? 28 : 32),
      marginBottom: 5,
      marginTop: 10,
    },
    heading4: {
      color: colors.text_color_primary,
      fontSize: 18,
      fontWeight: "600",
      lineHeight: Platform.OS === 'web' ? 1.5 : 27,
      marginBottom: 5,
      marginTop: 10,
    },
    heading5: {
      color: colors.text_color_primary,
      fontSize: 18,
      fontWeight: "600",
      lineHeight: Platform.OS === 'web' ? 1.75 : 27,
      marginBottom: 0,
      marginTop: 0,
    },
    heading6: {
      color: colors.text_color_primary,
      fontSize: 17,
      fontWeight: "500",
      lineHeight: Platform.OS === 'web' ? 1.75 : 25.5,
      marginBottom: 0,
      marginTop: 0,
    },
    // Lists
    bullet_list: {
      marginTop: 6,
      marginBottom: 6,
      paddingLeft: Platform.OS === 'web' ? 24 : 4,
    },
    ordered_list: {
      marginTop: 6,
      marginBottom: 6,
      paddingLeft: Platform.OS === 'web' ? 24 : 4,
    },
    list_item: {
      color: colors.text_color_primary,
      marginTop: 4,
      marginBottom: 4,
      ...(Platform.OS !== 'web' && {
        flexDirection: 'row',
        alignItems: 'flex-start',
      }),
      fontFamily: FONT_FAMILY,
    },
    bullet_list_icon: {
      marginRight: 8,
      marginTop: Platform.OS === 'web' ? 0 : 2,
      color: colors.text_color_primary,
      fontFamily: FONT_FAMILY,
      fontSize: 17,
      lineHeight: 25.5,
    },
    bullet_list_content: {
      flex: 1,
      flexShrink: 1,
    },
    ordered_list_icon: {
      marginRight: 8,
      marginTop: Platform.OS === 'web' ? 0 : 2,
      color: colors.text_color_primary,
      fontFamily: FONT_FAMILY,
      fontSize: 17,
      lineHeight: 25.5,
      minWidth: 16,
    },
    ordered_list_content: {
      flex: 1,
      flexShrink: 1,
    },
    // Code
    code_inline: {
      backgroundColor: colors.solid_color_secondary,
      color: colors.text_color_primary,
      paddingLeft: 6,
      paddingRight: 6,
      paddingTop: 2,
      paddingBottom: 2,
      borderRadius: 4,
      fontSize: 16,
      fontFamily: Platform.OS === 'macos' ? 'Menlo' : 'monospace',
    },
    code_block: {
      backgroundColor: colors.solid_color_secondary,
      padding: 12,
      borderRadius: 8,
      marginTop: 8,
      marginBottom: 8,
    },
    fence: {
      backgroundColor: colors.solid_color_secondary,
      padding: 12,
      borderRadius: 8,
      marginTop: 8,
      marginBottom: 8,
    },
    // Tables
    tableWrapper: {
      width: '100%',
      maxWidth: '100%',
      overflow: 'hidden',
      minWidth: 0,
      flexShrink: 1,
    },
    table: {
      ...(Platform.OS === 'web' ? { minWidth: '100%' } : {}),
      flexDirection: 'column',
    },
    thead: {
      backgroundColor: colors.solid_color_secondary,
      borderBottomWidth: 1,
      borderColor: colors.border_color_input,
    },
    tbody: {
      backgroundColor: codeBgColor,
    },
    tr: {
      borderBottomWidth: 1,
      borderColor: colors.border_color_input,
      flexDirection: 'row',
    },
    th: {
      flexGrow: 1,
      flexShrink: 0,
      flexBasis: 100,
      padding: 8,
      fontWeight: '600',
      minWidth: 100,
      backgroundColor: colors.solid_color_primary,
      borderRightWidth: 1,
      borderRightColor: colors.border_color_input,
    },
    td: {
      flexGrow: 1,
      flexShrink: 0,
      flexBasis: 100,
      padding: 8,
      borderRightWidth: 1,
      borderRightColor: colors.border_color_input,
      backgroundColor: codeBgColor,
      ...(Platform.OS === 'web' && {
        wordBreak: 'break-word',
      }),
    },
    // Inline text elements
    strong: {
      fontWeight: 'bold',
    },
    em: {
      fontStyle: 'italic',
    },
    s: { // strikethrough
      textDecorationLine: 'line-through',
    },
    link: {
      color: colors.text_color_link || '#3157a4',
    },
    textgroup: {
      flexShrink: Platform.OS === 'web' ? 1 : 0,
    },
    hardbreak: {
      height: 16,
    },
    softbreak: {
      height: 8,
    },
    pre: {
      backgroundColor: colors.solid_color_secondary,
      padding: 12,
      borderRadius: 8,
      marginTop: 8,
      marginBottom: 8,
    },
    inline: {
      // color: colors.text_color_primary,
    },
    span: {
      // color: colors.text_color_primary,
    },
    };
    if (Platform.OS === 'macos') {
      // macOS chat uses the shared web/mobile chat scale; only Work sessions
      // keep the compact Mac type scale.
      return macMarkdownStyles(shared, {
        colors,
        isDark: isDarkTheme(theme),
        isUser,
        isDestructive: isError || isDeleted,
        compact: isWorkSession,
        fontScale,
      });
    }
    return compactWorkText ? workMarkdownStyles(shared, Platform.OS === 'web') : shared;
  }, [colors, isXS, isError, isDeleted, messageLayout, isUser, theme, codeBgColor, compactWorkText, isWorkSession, fontScale]);

  // Track streaming state via ref so code block renderer can read it without recreation
  const isStreamingRef = useRef(isStreaming);
  isStreamingRef.current = isStreaming;
  
  // Memoize code block renderer
  const renderCodeBlock = useCallback((node) => {
    try {
    const language = node.sourceInfo || node.attributes?.language || "text";
    const normalizedLanguage = typeof language === 'string' ? language.trim().toLowerCase() : 'text';
    const codeContent = node.content ?? '';

    if (/^gab-card(?:[^\s]*)?$/i.test(normalizedLanguage) && !isUser && !isError && !isDeleted) {
      return isStreamingRef.current ? <StreamingCardPlaceholder key={node.key} kind="gab-ui" colors={colors} fontScale={fontScale} />
        : <Text key={node.key} style={{ color: colors.text_color_primary }}>{normalizeAnswerBlocks('```'+normalizedLanguage+'\n'+codeContent+'\n```', false)}</Text>;
    }

    if (isStreamingRef.current && !isUser && !isError && !isDeleted) {
      // Keep generated cards and HTML previews in place while their source
      // streams, without showing partial payloads or running incomplete code.
      if (normalizedLanguage === 'gab-ui') {
        // Markdown includes a split closing fence in the open block's content
        // until its third backtick arrives. It is framing, not answer JSON;
        // validate the payload without it so a readable card stays readable.
        const answer = validateStreamingAnswer(codeContent.replace(/\n[ \t]*`{1,2}[ \t]*$/, ''));
        if (!interactiveAnswerAllowed) return answer.spec ? <Text key={node.key} style={{ color: colors.text_color_primary }}>{answer.spec.fallbackMarkdown}</Text> : null;
        if (answer.spec) return <InteractiveAnswer key={node.key} spec={answer.spec} pending={answer.pending} colors={colors} conversationId={conversationId} messageId={messageId} />;
      }
      if (STREAMING_CARD_KINDS.includes(normalizedLanguage)) {
        const kind = HTML_PREVIEW_LANGUAGES.includes(normalizedLanguage) ? 'html' : normalizedLanguage;
        return <StreamingCardPlaceholder key={node.key} kind={kind} colors={colors} fontScale={fontScale} />;
      }
      // The fence's language can arrive over several chunks (e.g. `rec` then
      // `ipe`). Hold that empty block until it can be identified; ordinary
      // code still streams as soon as its language or content is known.
      const partialLanguage = (node.sourceInfo || node.attributes?.language || '').trim().toLowerCase();
      if (node.type === 'fence' && !codeContent && STREAMING_CARD_KINDS.some(kind => kind.startsWith(partialLanguage))) {
        return null;
      }
    }

    // Use the same sandboxed preview as Work deliverables once the response
    // finishes, so partially streamed scripts never repeatedly execute.
    if (
      HTML_PREVIEW_LANGUAGES.includes(normalizedLanguage) &&
      !isStreamingRef.current && !isUser && !isError && !isDeleted &&
      codeContent.trim()
    ) {
      return (
        <View key={node.key} style={{ marginVertical: 8 }}>
          <InlineHtmlPreview
            html={codeContent}
            conversationId={conversationId}
            isFromTemporaryChat={isFromTemporaryChat}
          />
        </View>
      );
    }

    // Handle math display blocks (produced by extractMathExpressions)
    if (language === 'math') {
      const mathColor = getMessageTextColor((isError || isDeleted), messageLayout, isUser, colors);
      return (
        <DisplayMath
          key={node.key}
          latex={codeContent}
          color={mathColor}
          fontSize={compactWorkText || Platform.OS === 'macos' ? markdownStyles.text.fontSize : 17}
        />
      );
    }

    if (normalizedLanguage === 'gab-ui' && !isStreamingRef.current && !isUser && !isError && !isDeleted) {
      const answer = validateAnswer(codeContent);
      return answer.spec && interactiveAnswerAllowed
        ? <InteractiveAnswer key={node.key} spec={answer.spec} colors={colors} conversationId={conversationId} messageId={messageId} />
        : <Text key={node.key} style={{ color: colors.text_color_primary }}>{answer.fallback}</Text>;
    }

    // Auto mode can emit a validated JSON chart spec in a fenced `chart`
    // block. Replace the placeholder once the stream ends and safely fall
    // back to code if the completed payload is malformed.
    if (normalizedLanguage === 'chart' && !isStreamingRef.current && !isUser && !isError && !isDeleted) {
      const chartSpec = parseChartSpec(codeContent);
      if (chartSpec) {
        return <InlineChart key={node.key} spec={chartSpec} colors={colors} />;
      }
    }

    // Only mount complete, validated cards so their controls and saved state
    // never operate on a partially generated specification.
    if (normalizedLanguage === 'recipe' && !isStreamingRef.current && !isUser && !isError && !isDeleted) {
      const recipeSpec = parseRecipeSpec(codeContent);
      if (recipeSpec) {
        return (
          <RecipeCard
            key={node.key}
            spec={recipeSpec}
            colors={colors}
            conversationId={conversationId}
            messageId={messageId}
          />
        );
      }
    }

    if (normalizedLanguage === 'workout' && !isStreamingRef.current && !isUser && !isError && !isDeleted) {
      const workoutSpec = parseWorkoutSpec(codeContent);
      if (workoutSpec) {
        return (
          <WorkoutCard
            key={node.key}
            spec={workoutSpec}
            colors={colors}
            conversationId={conversationId}
            messageId={messageId}
          />
        );
      }
    }

    if (EXPERIENCE_KINDS.includes(normalizedLanguage) && !isStreamingRef.current && !isUser && !isError && !isDeleted) {
      const spec = parseExperienceSpec(codeContent, normalizedLanguage);
      if (spec) return <ExperienceCard key={node.key} spec={spec} colors={colors} conversationId={conversationId} messageId={messageId} />;
    }

    if (normalizedLanguage === 'places' && !isStreamingRef.current) {
      const placesSpec = parsePlacesSpec(codeContent);
      if (placesSpec) {
        return <MessagePlaces key={node.key} places={placesSpec.places} map={placesSpec.map} />;
      }
    }

    const blockId = codeContent;
    const isCopied = copiedStates[blockId] || false;
    const currentlyStreaming = isStreamingRef.current;
    
    const handleCopy = async () => {
      try {
        await copyText(codeContent);
        setCopiedStates(prev => ({ ...prev, [blockId]: true }));
        setTimeout(() => {
          setCopiedStates(prev => ({ ...prev, [blockId]: false }));
        }, 5000);
      } catch (error) {
        console.error('Failed to copy code block:', error);
      }
    };
    
    // Mac uses the native highlighter's Text spans, never the web component's
    // CSS styles. Bound the work and leave in-progress code as plain text.
    const MAX_HIGHLIGHT_LENGTH = 12000;
    const shouldHighlight = ['web', 'macos'].includes(Platform.OS) && !currentlyStreaming && normalizedLanguage !== 'text' && codeContent.length < MAX_HIGHLIGHT_LENGTH;
    const macDiff = Platform.OS === 'macos' && ['diff', 'patch'].includes(normalizedLanguage)
      ? parseProjectGitDiff(codeContent) : null;

    const plainCodeStyle = {
      color: colors.text_color_primary,
      fontFamily: Platform.select({ ios: 'Menlo', macos: 'Menlo', android: 'monospace', default: 'monospace' }),
      fontSize: compactWorkText || Platform.OS === 'macos' ? markdownStyles.code_inline.fontSize : (isXS ? 15 : 16),
      padding: Platform.OS === 'macos' ? 16 : 12,
      lineHeight: Platform.OS === 'macos' ? 22 * fontScale : 20,
    };
    
    return (
      <View
        key={node.key}
        // dataSet → data-* HTML attributes on web (no-op on native). Used by
        // getMessageHtmlForCopy as a placeholder so paste targets like
        // Obsidian receive a proper <pre><code> with newlines preserved
        // instead of the flattened scrollview content.
        dataSet={{ messageCode: '', codeLanguage: language }}
        style={[
          wrapperStyles.container,
          { backgroundColor: codeBgColor }
        ]}>
        <View
          // Strip the language label + Copy button from the copied HTML so
          // "Plain Text" / "Copy" don't leak into rich-text paste targets.
          dataSet={{ copyExclude: '' }}
          style={wrapperStyles.header}
        >
          <Text style={wrapperStyles.headerText}>
            {language === 'text' ? 'Plain Text' : language}
          </Text>
          <Pressable
            onPress={handleCopy}
            accessibilityRole="button"
            accessibilityLabel={isCopied ? 'Code copied' : 'Copy code'}
            style={({ pressed }) => [
              wrapperStyles.button,
              { opacity: pressed ? 0.7 : 1 }
            ]}
          >
            <Icon name={isCopied ? "circle-check" : "copy"} size={10} color={colors.text_color_primary} style={wrapperStyles.icon} />
            <Text style={wrapperStyles.headerText}>
              {isCopied ? 'Copied!' : 'Copy'}
            </Text>
          </Pressable>
        </View>
        <ScrollView
          horizontal={Platform.OS !== 'web' && !macDiff}
          showsHorizontalScrollIndicator={Platform.OS === 'macos' || showsScrollIndicator}
          showsVerticalScrollIndicator={showsScrollIndicator}
          scrollEnabled={Platform.OS !== 'macos' || !macDiff}
          nestedScrollEnabled
          style={Platform.OS !== 'web' ? { flexGrow: 0, flexShrink: 1 } : undefined}
        >
          {macDiff ? <View style={{ paddingVertical: 8 }}>
            <ProjectDiffLines rows={macDiff.rows} />
            {macDiff.truncated && <Text style={plainCodeStyle}>Preview truncated. Copy includes the complete diff.</Text>}
          </View> : Platform.OS === 'web' ? (
            <WebCodeBlock
              language={normalizedLanguage}
              codeTheme={codeTheme}
              plainStyle={plainCodeStyle}
              highlight={shouldHighlight}
            >
              {codeContent}
            </WebCodeBlock>
          ) : shouldHighlight ? (
            <Text style={plainCodeStyle} selectable>
              {highlightCodeNative(
                codeContent,
                normalizedLanguage,
                codeTheme,
                plainCodeStyle.fontFamily,
                plainCodeStyle.fontSize,
              )}
            </Text>
          ) : (
            // Deliberately not the UITextView swap used for prose. This sits in
            // a horizontal ScrollView, so there is no width to wrap against and
            // a read-only TextInput measures itself wrong, taking the block's
            // height with it. The header Copy button covers the whole block.
            <Text style={plainCodeStyle} selectable={Platform.OS !== 'web'}>
              {codeContent}
            </Text>
          )}
        </ScrollView>
      </View>
    );
    } catch (error) {
      console.error('[MessageContentRenderer] Code block render error:', error);
      return (
        <View key={node.key} style={{ padding: 12, backgroundColor: codeBgColor, borderRadius: 8, marginVertical: 8 }}>
          <Text style={{ color: colors.text_color_primary, fontFamily: Platform.select({ ios: 'Menlo', macos: 'Menlo', android: 'monospace', default: 'monospace' }), fontSize: compactWorkText || Platform.OS === 'macos' ? markdownStyles.code_inline.fontSize : (isXS ? 15 : 16), lineHeight: 22 * fontScale }}>
            {node.content ?? ''}
          </Text>
        </View>
      );
    }
  }, [codeBgColor, colors, codeTheme, copiedStates, isXS, showsScrollIndicator, wrapperStyles, isError, isDeleted, messageLayout, isUser, conversationId, messageId, isFromTemporaryChat, interactiveAnswerAllowed, compactWorkText, markdownStyles, fontScale]);

  // Memoize the citation handler
  const handleCitationClick = useCallback((citationIndex) => {
    if (!conversationId || !messageId) return;
    
    // If clicking the same citation, clear it
    const currentCitation = `${conversationId}.${messageId}.${citationIndex}`;

    if (selectedCitation === currentCitation) {
      setSelectedCitation(null);
    } else {
      setSelectedCitation(conversationId, messageId, citationIndex);
    }
  }, [conversationId, messageId, selectedCitation]);

  // Memoize table renderer
  const renderTable = useCallback((node, children) => {
    // Generate a stable ID from table content
    const getTableInfo = (tableNode) => {
      try {
        const rows = extractMarkdownTableData(tableNode);
        return { id: rows.map(row => row.join('|')).join('||') || 'empty-table', rows };
      } catch (error) {
        console.error('Error generating table ID:', error);
        return { id: 'invalid-table', rows: [[]] };
      }
    };

    const { id: tableId, rows } = getTableInfo(node);
    const isCopied = copiedStates[tableId] || false;

    const handleCopyTable = async () => {
      try {
        const markdown = rowsToMarkdown(extractMarkdownTableData(node));
        await copyText(markdown);
        
        setCopiedStates(prev => ({ ...prev, [tableId]: true }));
        setTimeout(() => {
          setCopiedStates(prev => ({ ...prev, [tableId]: false }));
        }, 5000);
      } catch (error) {
        console.error('Failed to copy table:', error);
        // TODO: show error
      }
    };

    const handleExportTable = async () => {
      try {
        const tsv = rowsToTsv(extractMarkdownTableData(node));
        
        if (Platform.OS === 'web') {
          // Create and trigger download on web
          const blob = new Blob([tsv], { type: 'text/tab-separated-values' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `table-${messageId || 'export'}.tsv`;
          
          // Cleanup function
          const cleanup = () => {
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
          };

          try {
            document.body.appendChild(a);
            a.click();
          } finally {
            cleanup();
          }
        } else {
          // Native export using file system and sharing
          const filename = `table-${messageId || 'export'}.tsv`;
          const fileUri = `${FileSystem.documentDirectory}${filename}`;
          await FileSystem.writeAsStringAsync(fileUri, tsv, {
            encoding: FileSystem.EncodingType.UTF8,
          });
          const canShare = await Sharing.isAvailableAsync();
          if (canShare) {
            await Sharing.shareAsync(fileUri, {
              mimeType: 'text/tab-separated-values',
              dialogTitle: 'Export Table',
            });
          } else {
            // Fall back to copy if sharing not available
            await copyText(tsv);
          }
        }
      } catch (error) {
        console.error('Failed to export table:', error);
        // TODO: show error
      }
    };

    const handleAddTableToSpreadsheet = async () => {
      try {
        const rows = extractMarkdownTableData(node);
        if (!rows.length) return;
        const target = getActiveEditorInsert();
        if (target?.noun === 'spreadsheet') {
          target.insert(rowsToTsv(rows));
          addToast('Added to spreadsheet', 'success');
          return;
        }
        if (!canUseDesktopEditors()) {
          addToast('Editors are available on desktop web', 'error');
          return;
        }
        await createAndOpenEditor('spreadsheet', {
          rows,
          conversationId,
          messageId,
        });
        addToast('Opened in spreadsheet editor', 'success');
      } catch (error) {
        console.error('Failed to add table to spreadsheet:', error);
        addToast('Could not open the editor', 'error');
      }
    };

    const showAddToSpreadsheet = Platform.OS === 'web' && !isXS && !isUser && !isError && !isDeleted;

    const tableInner = (
      <View key={node.key} style={[markdownStyles.tableWrapper, { marginTop: 8, marginBottom: 8 }]}>
        <View style={[
          wrapperStyles.container,
          { backgroundColor: codeBgColor }
        ]}>
          <View style={[
            wrapperStyles.header,
            {
              borderBottomWidth: 1,
              borderBottomColor: colors.border_color_input,
            }
          ]}>
            <Text style={wrapperStyles.headerText}>Table</Text>
            <View style={wrapperStyles.headerActions}>
              {showAddToSpreadsheet ? (
                <Pressable
                  onPress={handleAddTableToSpreadsheet}
                  accessibilityRole="button"
                  accessibilityLabel="Add table to spreadsheet"
                  style={({ pressed }) => [
                    wrapperStyles.button,
                    { opacity: pressed ? 0.7 : 1 }
                  ]}
                >
                  <Icon name="file-excel" size={10} color={colors.text_color_primary} style={wrapperStyles.icon} />
                  <Text style={wrapperStyles.headerText}>
                    Add to spreadsheet
                  </Text>
                </Pressable>
              ) : null}
              <Pressable
                onPress={handleExportTable}
                accessibilityRole="button"
                accessibilityLabel="Export table"
                style={({ pressed }) => [
                  wrapperStyles.button,
                  { opacity: pressed ? 0.7 : 1 }
                ]}
              >
                <Icon name="download" size={10} color={colors.text_color_primary} style={wrapperStyles.icon} />
                <Text style={wrapperStyles.headerText}>
                  Export
                </Text>
              </Pressable>
              <Pressable
                onPress={handleCopyTable}
                accessibilityRole="button"
                accessibilityLabel={isCopied ? 'Table copied' : 'Copy table as Markdown'}
                style={({ pressed }) => [
                  wrapperStyles.button,
                  { opacity: pressed ? 0.7 : 1 }
                ]}
              >
                <Icon name={isCopied ? "circle-check" : "copy"} size={10} color={colors.text_color_primary} style={wrapperStyles.icon} />
                <Text style={wrapperStyles.headerText}>
                  {isCopied ? 'Copied!' : 'Copy'}
                </Text>
              </Pressable>
            </View>
          </View>

          <ScrollableTable
            tableStyle={markdownStyles.table}
            fadeColor={codeBgColor}
            rows={rows}
            fontSize={markdownStyles.text.fontSize}
          >
            {children}
          </ScrollableTable>
        </View>
      </View>
    );

    // On web, wrap in a <div> with CSS contain: inline-size to prevent
    // the table's wide content from inflating parent flex containers.
    // React Native Web Views can't use contain (not a supported RN style),
    // so we must use a native HTML element for this.
    if (Platform.OS === 'web') {
      return (
        <div
          key={node.key}
          data-message-table=""
          style={{
            contain: 'inline-size',
            overflow: 'hidden',
            width: '100%',
            // contain: inline-size makes this div contribute zero intrinsic
            // width, so shrink-wrapping bubbles collapse to their 200px
            // minimum and crush the table. Claim a sane minimum ourselves;
            // the inner scroll container handles tables wider than this.
            minWidth: 'min(560px, 70vw)',
            marginTop: 8,
            marginBottom: 8,
          }}
        >
          {tableInner}
        </div>
      );
    }

    return tableInner;
  }, [colors, codeBgColor, copiedStates, markdownStyles, wrapperStyles, isXS, isUser, isError, isDeleted, conversationId, messageId, addToast]);

  const isSelectable = Platform.OS !== 'web' && !isError && !isDeleted && !isStreaming;
  // Keep Android's fixed-width paragraph/list layout throughout streaming;
  // completion only enables selection, without replacing its native views.
  const usesAndroidProseLayout = Platform.OS === 'android' && !isError && !isDeleted;

  // UITextView derives its text container inset from padding, so the bottom
  // slack plays the same role as iosLastLineClipGuardStyle (see the paragraph
  // rule) without putting a stray newline into copied text.
  const iosSelectableParagraphStyle = useMemo(() => ({
    ...markdownStyles.text,
    padding: 0,
    paddingBottom: 2,
  }), [markdownStyles.text]);

  // iOS/macOS leave block metrics to nested spans so headings keep their size.
  // Android headings are separate blocks; merged runs are prose/lists and
  // need one root lineHeight to avoid CustomLineHeightSpan crop bugs.
  const mergedBlockBaseTextStyle = useMemo(() => ({
    fontFamily: FONT_FAMILY,
    ...(Platform.OS === 'android' && {
      fontSize: markdownStyles.text.fontSize,
      lineHeight: markdownStyles.text.lineHeight,
    }),
    color: markdownStyles.text.color,
    padding: 0,
    paddingBottom: 2,
  }), [markdownStyles.text]);

  const nativeProseTextStyle = useMemo(() => Platform.OS === 'ios'
    ? iosSelectableParagraphStyle
    : { ...markdownStyles.text, ...markdownStyles.textgroup },
  [iosSelectableParagraphStyle, markdownStyles.text, markdownStyles.textgroup]);

  // Helper: wrap children in <Text> only when all children are text-safe.
  // On Android, <Text> cannot contain <View>/<Pressable> — fall back to <View>.
  const nativeHeading = useCallback((style, children, nodeKey) => {
    if (Platform.OS === 'web') return children;
    const safe = React.Children.toArray(children).every(child => {
      if (child == null || typeof child === 'string' || typeof child === 'number') return true;
      if (React.isValidElement(child)) {
        const t = child.type;
        const n = typeof t === 'string' ? t : (t?.displayName || t?.name || '');
        return t === Text || n === 'Text' || n === 'InlineMath';
      }
      return false;
    });
    // Headings live under `textgroup`, never under `paragraph`, so they need
    // their own opt-in. The heading style goes on last so the text view's base
    // attributes (size, line height, colour) match the <Text> it replaces.
    // Marked mergeable so a selection can be dragged from a heading into the
    // prose beneath it; its margins are handed over as gaps because they stop
    // applying once it is folded into a shared text view.
    if (safe && isSelectable && (
      Platform.OS === 'macos' || (Platform.OS === 'ios' && isInertTextTree(children))
    )) {
      return (
        <NativeSelectableParagraph
          key={nodeKey}
          mergeable
          gapBefore={style?.marginTop || 0}
          gapAfter={style?.marginBottom || 0}
          containerStyle={Platform.OS === 'macos' ? style : undefined}
          textStyle={[markdownStyles.text, style, Platform.OS === 'ios' && iosTextInputResetStyle]}
        >
          {children}
        </NativeSelectableParagraph>
      );
    }
    if (safe) return <Text key={nodeKey} style={style} selectable={isSelectable}>{children}</Text>;
    return <View key={nodeKey} style={[style, { width: '100%' }]}>{children}</View>;
  }, [isSelectable, markdownStyles.text]);

  // Memoize markdown rules
  const markdownRules = useMemo(() => {
    const renderProjectReference = (value, key, children, style) => {
      const reference = projectSourceReference(value);
      if (!reference) return null;
      return <Text key={key} style={[style, { color: colors.text_color_link || colors.text_color_primary, textDecorationLine: 'underline' }]}
        accessibilityRole="link" accessibilityLabel={`Open ${reference.path}, line ${reference.line}`}
        onPress={() => openModal('ProjectFilePreviewModal', reference)}>{children}</Text>;
    };
    const openExternalUrl = (url) => {
      showConfirmationModal({
        title: 'External Link',
        message: 'You are about to visit an external website. Please be careful with links from unknown sources.',
        itemName: url,
        confirmText: 'Open Link',
        cancelText: 'Cancel',
        onConfirm: () => {
          if (Platform.OS === 'web') {
            window.open(url, '_blank');
          } else {
            Linking.openURL(url);
          }
        },
      });
    };

    // Shared rendering for clickable external URLs — used by both markdown
    // links and bare/code-wrapped URLs (Work answers frequently emit
    // `https://…` in inline code, which is otherwise plain monospace text).
    const renderExternalLink = (url, nodeKey, children, extraStyle) => {
      const handlePress = () => openExternalUrl(url);
      const linkColor = isUser
        ? getMessageTextColor(false, messageLayout, isUser, colors)
        : (colors.text_color_link || (isDarkTheme(theme) ? '#58a6ff' : '#3157a4'));
      const linkStyle = {
        ...(extraStyle || {}),
        color: linkColor,
        textDecorationLine: 'underline',
        textDecorationColor: linkColor,
      };

      if (Platform.OS === 'web') {
        return (
          <a
            key={nodeKey}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(event) => {
              if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button === 1) {
                return;
              }
              event.preventDefault();
              handlePress();
            }}
            style={{
              ...(extraStyle || {}),
              color: linkColor,
              textDecoration: 'underline',
              cursor: 'pointer',
              wordBreak: 'break-word',
            }}
          >
            {children}
          </a>
        );
      }

      return (
        <Text
          key={nodeKey}
          style={linkStyle}
          onPress={handlePress}
          accessibilityRole="link"
        >
          {children}
        </Text>
      );
    };

    return {
    // Matches the library's default body rule, except that on native runs of
    // adjacent selectable paragraphs are collapsed into one text view so a
    // selection can be dragged across them.
    body: (node, children, parent, styles) => {
      if (Platform.OS === 'web') {
        // The markdown parser may include empty string sentinels and unkeyed
        // elements in its child array while a response is streaming. RNW's
        // View rejects the former and React warns about the latter, so use a
        // native DOM block here and normalize the array before rendering it.
        return (
          <div key="markdown-body" style={{ ...markdownStyles.text, ...StyleSheet.flatten(styles._VIEW_SAFE_body) }}>
            {React.Children.toArray(children)}
          </div>
        );
      }
      return (
        <View key="markdown-body" style={styles._VIEW_SAFE_body}>
          {mergeSelectableParagraphs(children, mergedBlockBaseTextStyle)}
        </View>
      );
    },
    // NOTE on web rendering: heading children come in already wrapped in
    // <Text> (which RNW serializes as <div>, a block-level element).
    // Putting a block-level child inside <h*> makes Obsidian's HTML→MD
    // converter break "## " onto its own line, so we render children
    // directly here — CSS inheritance carries the heading styles down.
    heading1: (node, children) => {
      if (Platform.OS === 'web') {
        return <h1 key={node.key} style={markdownStyles.heading1}>{children}</h1>;
      }
      return nativeHeading(markdownStyles.heading1, children, node.key);
    },
    heading2: (node, children) => {
      if (Platform.OS === 'web') {
        return <h2 key={node.key} style={markdownStyles.heading2}>{children}</h2>;
      }
      return nativeHeading(markdownStyles.heading2, children, node.key);
    },
    heading3: (node, children) => {
      if (Platform.OS === 'web') {
        return <h3 key={node.key} style={markdownStyles.heading3}>{children}</h3>;
      }
      return nativeHeading(markdownStyles.heading3, children, node.key);
    },
    heading4: (node, children) => {
      if (Platform.OS === 'web') {
        return <h4 key={node.key} style={markdownStyles.heading4}>{children}</h4>;
      }
      return nativeHeading(markdownStyles.heading4, children, node.key);
    },
    heading5: (node, children) => {
      if (Platform.OS === 'web') {
        return <h5 key={node.key} style={markdownStyles.heading5}>{children}</h5>;
      }
      return nativeHeading(markdownStyles.heading5, children, node.key);
    },
    heading6: (node, children) => {
      if (Platform.OS === 'web') {
        return <h6 key={node.key} style={markdownStyles.heading6}>{children}</h6>;
      }
      return nativeHeading(markdownStyles.heading6, children, node.key);
    },
    bullet_list: (node, children) => {
      const content = flattenNativeList({
        children,
        listStyle: markdownStyles.bullet_list,
        baseTextStyle: mergedBlockBaseTextStyle,
        lineStyle: nativeProseTextStyle,
        enabled: usesAndroidProseLayout || (isSelectable && Platform.OS !== 'macos'),
        selectable: isSelectable,
        nodeKey: node.key,
      }) || (
        <View key={node.key} style={markdownStyles.bullet_list}>
          {children}
        </View>
      );
      
      if (Platform.OS === 'web') {
        return <ul key={node.key} style={markdownStyles.bullet_list}>{children}</ul>;
      }
      return content;
    },
    ordered_list: (node, children) => {
      const content = flattenNativeList({
        children,
        listStyle: markdownStyles.ordered_list,
        baseTextStyle: mergedBlockBaseTextStyle,
        lineStyle: nativeProseTextStyle,
        enabled: usesAndroidProseLayout || (isSelectable && Platform.OS !== 'macos'),
        selectable: isSelectable,
        nodeKey: node.key,
      }) || (
        <View key={node.key} style={markdownStyles.ordered_list}>
          {children}
        </View>
      );
      
      if (Platform.OS === 'web') {
        return <ol key={node.key} style={markdownStyles.ordered_list}>{children}</ol>;
      }
      return content;
    },
    list_item: (node, children, parent) => {
      if (Platform.OS === 'web') {
        return <li key={node.key} style={markdownStyles.list_item}>{children}</li>;
      }

      const nearestList = parent?.find(el => el.type === 'bullet_list' || el.type === 'ordered_list');
      const isBulletList = nearestList?.type === 'bullet_list';
      const isOrderedList = nearestList?.type === 'ordered_list';

      // Handed to the list rule rather than laid out here, so it can decide
      // between the bullet row and a flattened line in a shared text view.
      if (isBulletList || isOrderedList) {
        let prefix = '•';
        if (isOrderedList) {
          const startNum = Number(nearestList?.attributes?.start ?? 1);
          prefix = `${startNum + node.index}.`;
        }

        return (
          <NativeListItem
            key={node.key}
            prefix={prefix}
            selectable={isSelectable}
            itemStyle={markdownStyles.list_item}
            iconStyle={isOrderedList ? markdownStyles.ordered_list_icon : markdownStyles.bullet_list_icon}
            contentStyle={isOrderedList ? markdownStyles.ordered_list_content : markdownStyles.bullet_list_content}
          >
            {children}
          </NativeListItem>
        );
      }

      return (
        <View key={node.key} style={{ marginTop: 4, marginBottom: 4 }}>
          {children}
        </View>
      );
    },
    strong: (node, children) => {
      const content = (
        <Text key={node.key} style={markdownStyles.strong}>
          {children}
        </Text>
      );
      
      if (Platform.OS === 'web') {
        return <strong key={node.key} style={markdownStyles.strong}>{children}</strong>;
      }
      return content;
    },
    em: (node, children) => {
      const content = (
        <Text key={node.key} style={markdownStyles.em}>
          {children}
        </Text>
      );
      
      if (Platform.OS === 'web') {
        return <em key={node.key} style={markdownStyles.em}>{children}</em>;
      }
      return content;
    },
    s: (node, children) => {
      const content = (
        <Text key={node.key} style={markdownStyles.s}>
          {children}
        </Text>
      );
      
      if (Platform.OS === 'web') {
        return <s key={node.key} style={markdownStyles.s}>{children}</s>;
      }
      return content;
    },
    code_inline: (node, children) => {
      const raw = String(node.content || '').trim();
      const projectLink = renderProjectReference(raw, node.key, node.content, markdownStyles.code_inline);
      if (projectLink) return projectLink;
      // A code span whose entire content is a URL renders as a clickable
      // link (kept monospace) — models, especially Work answers, wrap bare
      // URLs in backticks and those were dead text.
      const isBareUrl = /^https?:\/\/\S+$/i.test(raw) || /^www\.[^\s]+\.[a-z]{2,}[^\s]*$/i.test(raw);
      if (isBareUrl) {
        const url = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
        return renderExternalLink(url, node.key, node.content, {
          fontFamily: markdownStyles.code_inline.fontFamily,
          fontSize: markdownStyles.code_inline.fontSize,
          ...(Platform.OS === 'web' && { wordBreak: 'break-word' }),
        });
      }
      const content = (
        <Text key={node.key} style={[markdownStyles.text, markdownStyles.code_inline]}>
          {node.content}
        </Text>
      );

      if (Platform.OS === 'web') {
        return <code key={node.key} style={markdownStyles.code_inline}>{node.content}</code>;
      }
      return content;
    },
    table: renderTable,
    thead: (node, children) => (
      <View key={node.key} style={markdownStyles.thead}>
        {children}
      </View>
    ),
    tbody: (node, children) => (
      <View key={node.key} style={markdownStyles.tbody}>
        {children}
      </View>
    ),
    tr: (node, children) => (
      <View key={node.key} style={markdownStyles.tr}>
        {children}
      </View>
    ),
    th: (node, children) => (
      <TableCell key={node.key} style={markdownStyles.th} columnIndex={node.index}>{children}</TableCell>
    ),
    td: (node, children) => (
      <TableCell key={node.key} style={markdownStyles.td} columnIndex={node.index}>{children}</TableCell>
    ),
    // Show link markdown as text
    link: (node, children) => {
      const url = node.attributes.href;
      const workFile = workArtifactReference(url);
      if (workFile) {
        const { taskId, artifact } = workFile;
        const openWorkFile = async () => {
          try {
            const file = { ...coworkerArtifactAsFile(artifact, ''), taskId, path: artifact.path };
            if (Platform.OS === 'web') {
              const ready = await hydrateOutputFileForOpen(file, { taskId });
              if (ready) openMessageFile(ready);
            } else {
              const downloadUrl = await coworkerApi.getArtifactDownloadUrl(taskId, artifact.path);
              if (!downloadUrl) throw new Error('File preview is not available yet. Try opening it from Outputs.');
              openMessageFile({ ...file, url: downloadUrl, cdnPath: downloadUrl });
            }
          } catch (error) {
            addToast(error?.message || 'Could not open file', 'error');
          }
        };
        if (Platform.OS === 'web') return <a key={node.key} href="#"
          aria-label={`Open ${artifact.name || artifact.path}`}
          style={{ color: colors.text_color_link || colors.text_color_primary, textDecorationLine: 'underline' }}
          onClick={event => { event.preventDefault(); void openWorkFile(); }}>{children}</a>;
        return <Text key={node.key}
          style={{ color: colors.text_color_link || colors.text_color_primary, textDecorationLine: 'underline' }}
          accessibilityRole="link" accessibilityLabel={`Open ${artifact.name || artifact.path}`}
          onPress={openWorkFile}>{children}</Text>;
      }
      const projectLink = renderProjectReference(url, node.key, children);
      if (projectLink) return projectLink;
      const isAgentLink = url.includes(AGENT_URL_PREFIX);

      if (isAgentLink) {
        const handlePress = () => {
          const agentId = url?.includes(AGENT_URL_PREFIX) ? url.split(AGENT_URL_PREFIX)[1] : undefined;
          if (agentId) {
            openModal('AgentDetailsModal', { agentId })
          }
        };

        const linkColor = isUser
          ? getMessageTextColor(false, messageLayout, isUser, colors)
          : (colors.text_color_link || (isDarkTheme(theme) ? '#58a6ff' : '#3157a4'));
        const linkStyle = {
          color: linkColor,
          textDecorationLine: 'underline',
          textDecorationColor: linkColor,
        };

        if (Platform.OS === 'web') {
          return (
            <a
              key={node.key}
              href={url}
              onClick={(event) => {
                event.preventDefault();
                handlePress();
              }}
              style={{
                color: linkColor,
                textDecoration: 'underline',
                cursor: 'pointer',
                wordBreak: 'break-word',
              }}
            >
              {children}
            </a>
          );
        }

        return (
          <Text
            key={node.key}
            style={linkStyle}
            onPress={handlePress}
            accessibilityRole="link"
          >
            {children}
          </Text>
        );
      }

      return renderExternalLink(url, node.key, children);
    },
    // Show image markdown as text
    image: (node) => (
      <Text key={node.key} color='primary'>
        {`![${node.attributes.alt}](${node.attributes.src})`}
      </Text>
    ),
    // Show video markdown as text
    video: (node) => (
      <Text key={node.key} color='primary'>
        {`@[video](${node.attributes.src})`}
      </Text>
    ),
    // Contain raw HTML/SVG that the AI may include in responses.
    // Without this, elements like <svg width="400em"> blow out the
    // chat bubble width.
    
    // : todo : improve safety here
    html_inline: (node) => {
      if (Platform.OS === 'web' && shouldRenderRawMessageHtml(node.content)) {
        return (
          <span
            key={node.key}
            dangerouslySetInnerHTML={{ __html: node.content }}
            style={{ overflow: 'hidden', maxWidth: '100%', display: 'inline' }}
          />
        );
      }
      return <Text key={node.key} style={markdownStyles.text}>{node.content}</Text>;
    },

    // : todo : improve safety here
    html_block: (node) => {
      if (Platform.OS === 'web') {
        if (shouldRenderRawMessageHtml(node.content)) {
          return (
            <div
              key={node.key}
              dangerouslySetInnerHTML={{ __html: node.content }}
              style={{ overflow: 'hidden', maxWidth: '100%' }}
            />
          );
        }
        return <Text key={node.key} style={markdownStyles.text}>{node.content}</Text>;
      }
      // Raw HTML is shown verbatim as a block of its own, and had no selection
      // at all before — not even on Android.
      return (
        <NativeSelectableParagraph
          key={node.key}
          textStyle={Platform.OS === 'ios' ? iosSelectableParagraphStyle : markdownStyles.text}
        >
          {node.content}
        </NativeSelectableParagraph>
      );
    },
    // Custom rule to handle code blocks
    fence: renderCodeBlock,
    code_block: renderCodeBlock,
    paragraph: (node, children, parent, styles) => {
      // Skip empty paragraphs that produce blank space on Android (especially Samsung)
      const childArray = React.Children.toArray(children);
      if (childArray.length === 0) return null;

      const isFirstParagraph = node.index === 0;

      const theParagraphStyles = {
        ...markdownStyles.paragraph,
        ...(isFirstParagraph ? markdownStyles.paragraphFirst : markdownStyles.paragraphNotFirst),
      };

      if (Platform.OS === 'web') {
        return <div key={node.key} style={theParagraphStyles}>{childArray}</div>;
      }

      // On native Android, <Text> can only contain other <Text> or strings.
      // Markdown children can include <View>, <Pressable>, etc. (e.g. code
      // blocks, citations, images) which crash Android when nested inside
      // <Text>. Check if all children are text-safe before wrapping in <Text>.
      const allTextSafe = childArray.every(child => {
        if (child == null) return true;
        if (typeof child === 'string' || typeof child === 'number') return true;
        if (React.isValidElement(child)) {
          const type = child.type;
          const typeName = typeof type === 'string' ? type : (type?.displayName || type?.name || '');
          if (type === Text || typeName === 'Text' || typeName === 'InlineMath') return true;
        }
        return false;
      });

      const canSelect = !isError && !isDeleted && !isStreaming;

      if (allTextSafe) {
        // On iOS this renders prose through a read-only UITextView so long-press
        // gives the standard selection (magnifier, grab handles, system menu)
        // instead of RN's copy-the-whole-paragraph menu. A UITextView swallows
        // taps, so paragraphs with links or citations stay on the plain <Text>
        // path there. Android and macOS keep their selectable <Text> (including
        // inline links) and opt in so the body can merge neighbouring blocks.
        const canUseSelectableParagraph = usesAndroidProseLayout || (canSelect && (
          Platform.OS === 'macos' ||
          (Platform.OS === 'ios' && isInertTextTree(childArray))
        ));

        if (canUseSelectableParagraph) {
          return (
            <NativeSelectableParagraph
              key={node.key}
              mergeable
              selectable={canSelect}
              gapBefore={theParagraphStyles.marginTop || 0}
              containerStyle={theParagraphStyles}
              textStyle={Platform.OS === 'ios'
                ? iosSelectableParagraphStyle
                : Platform.OS === 'android' ? mergedBlockBaseTextStyle
                : [markdownStyles.text, markdownStyles.textgroup]}
            >
              {children}
            </NativeSelectableParagraph>
          );
        }

        // iOS New Architecture workaround for
        // https://github.com/facebook/react-native/issues/53450 — a rounding
        // error in native text measurement can make a wrapped paragraph's
        // measured height a fraction of a pixel too short, so TextKit drops
        // the entire last line ("text cut off mid-sentence"). Appending a
        // tiny zero-width guard line gives the layout a couple of pixels of
        // slack, so the sub-pixel rounding deficit can only ever eat into
        // the invisible guard line instead of a real one. The \n makes the
        // guard its own paragraph, so its 2px line height doesn't affect
        // the real lines. OTA-shippable JS fallback for the native epsilon
        // patch (patches/react-native+0.79.6.patch); remove once that patch
        // ships in a store build.
        if (Platform.OS === 'ios') {
          return (
            <View key={node.key} style={theParagraphStyles}>
              <Text style={[markdownStyles.text, markdownStyles.textgroup]} selectable={canSelect}>
                {children}
                <Text style={iosLastLineClipGuardStyle}>{'\n\u200B'}</Text>
              </Text>
            </View>
          );
        }
        return (
          <View key={node.key} style={theParagraphStyles}>
            <Text style={[markdownStyles.text, markdownStyles.textgroup]} selectable={canSelect}>
              {children}
            </Text>
          </View>
        );
      }

      // Mixed content: render everything inside a View without wrapping in Text
      return (
        <View key={node.key} style={[theParagraphStyles, { width: '100%' }]}>
          {children}
        </View>
      );
    },
    textgroup: (node, children, parent) => {
      const inHeading = parent?.some(el => /^heading[1-6]$/.test(el.type));
      if (Platform.OS === 'web') {
        return (
          <span key={node.key} style={markdownStyles.textgroup}>
            {React.Children.toArray(children)}
          </span>
        );
      }
      const safe = React.Children.toArray(children).every(child => {
        if (child == null || typeof child === 'string' || typeof child === 'number') return true;
        if (React.isValidElement(child)) {
          const t = child.type;
          const n = typeof t === 'string' ? t : (t?.displayName || t?.name || '');
          return t === Text || n === 'Text' || n === 'InlineMath';
        }
        return false;
      });
      const canSelect = !isError && !isDeleted && !isStreaming;
      // Text arriving here belongs to a table cell or a list item, both of
      // which sit in a flex slot inside a row. A read-only TextInput reports no
      // usable intrinsic height there, collapsing the row, so this path stays
      // on plain <Text>. Lists get full selection by being flattened into a
      // shared text view instead (see flattenNativeList).
      if (safe) {
        // Leaf spans inherit their heading. Body/table/list
        // text gets its base metrics here, on the native paragraph itself.
        return <Text key={node.key} style={[!inHeading && markdownStyles.text, markdownStyles.textgroup]} selectable={canSelect}>{children}</Text>;
      }
      return <View key={node.key} style={[markdownStyles.textgroup, { width: '100%' }]}>{children}</View>;
    },
    pre: (node, children) => children,
    text: (node) => {
      // RN Text becomes a div outside RN's Text ancestor context. Our web
      // paragraphs/formatting use HTML elements, so use spans here to keep
      // copied HTML inline even when a paste target strips the app's CSS.
      // Base type belongs to the paragraph/heading, never its leaf spans:
      // setting it here flattens headings and overrides link colors.
      const InlineText = Platform.OS === 'web' ? 'span' : Text;
      const textContent = node.content;
      const hasMath = textContent.includes('%%MI');
      const hasCitations = /\[\d+\]/.test(textContent);

      if (!hasMath && !hasCitations) {
        return (
          <InlineText key={node.key}>
            {textContent}
          </InlineText>
        );
      }

      // Unified token regex: match math inline placeholders OR citation references
      const tokenRegex = /%%MI(\d+)%%|\[(\d+)\]/g;
      const elements = [];
      let lastIndex = 0;
      let match;
      let keyIdx = 0;

      while ((match = tokenRegex.exec(textContent)) !== null) {
        // Add preceding text
        if (match.index > lastIndex) {
          const precedingText = textContent.slice(lastIndex, match.index);
          if (precedingText) {
            elements.push(
              <InlineText key={`t-${keyIdx++}`}>
                {precedingText}
              </InlineText>
            );
          }
        }

        if (match[1] !== undefined) {
          // Math inline placeholder %%MI{index}%%
          const idx = parseInt(match[1]);
          const latex = mathExpressionsRef.current[idx];
          if (latex) {
            const mathColor = getMessageTextColor((isError || isDeleted), messageLayout, isUser, colors);
            elements.push(
              <InlineMath
                key={`m-${keyIdx++}`}
                latex={latex}
                color={mathColor}
                fontSize={compactWorkText || Platform.OS === 'macos' ? markdownStyles.text.fontSize : 17}
              />
            );
          } else {
            // Fallback: show placeholder as text
            elements.push(
              <InlineText key={`t-${keyIdx++}`}>
                {match[0]}
              </InlineText>
            );
          }
        } else if (match[2] !== undefined) {
          // Citation reference [number]
          const citationNumber = match[2];
          const citationIndex = parseInt(citationNumber) - 1;
          const citationId = `${conversationId}.${messageId}.${citationIndex}`;
          const isHighlighted = selectedCitation === citationId;

          if (Platform.OS === 'web') {
            elements.push(
              <Pressable
                key={`c-${keyIdx++}`}
                onPress={() => handleCitationClick(citationIndex)}
                style={({ pressed }) => ({
                  backgroundColor: isHighlighted ? colors.solid_color_toast : colors.solid_color_secondary,
                  borderRadius: 4,
                  paddingLeft: 4,
                  paddingRight: 4,
                  paddingTop: 2,
                  paddingBottom: 2,
                  marginLeft: 4,
                  minWidth: 18,
                  alignItems: 'center',
                  opacity: pressed ? 0.7 : 1,
                  transform: [{ translateY: -1 }],
                })}
              >
                <Text
                  style={{
                    color: isHighlighted ? colors.text_color_alt : colors.text_color_primary,
                    fontSize: isXS ? 13 : 12,
                    fontWeight: '500',
                    textAlign: 'center',
                  }}
                >
                  {citationNumber}
                </Text>
              </Pressable>
            );
          } else {
            // On native, use Text with onPress to avoid nesting View inside Text (crashes Android)
            elements.push(
              <Text
                key={`c-${keyIdx++}`}
                onPress={() => handleCitationClick(citationIndex)}
                style={{
                  backgroundColor: isHighlighted ? colors.solid_color_toast : colors.solid_color_secondary,
                  borderRadius: 4,
                  paddingHorizontal: 4,
                  paddingVertical: 2,
                  marginLeft: 4,
                  color: isHighlighted ? colors.text_color_alt : colors.text_color_primary,
                  fontSize: isXS ? 13 : 12,
                  fontWeight: '500',
                  textAlign: 'center',
                  overflow: 'hidden',
                }}
              >
                {citationNumber}
              </Text>
            );
          }
        }

        lastIndex = match.index + match[0].length;
      }

      // Remaining text after last match
      if (lastIndex < textContent.length) {
        const remainingText = textContent.slice(lastIndex);
        if (remainingText) {
          elements.push(
            <InlineText key={`t-${keyIdx++}`}>
              {remainingText}
            </InlineText>
          );
        }
      }

      return (
        <InlineText key={node.key}>
          {elements}
        </InlineText>
      );
    },
    };
  }, [markdownStyles, colors, theme, renderCodeBlock, renderTable, openModal, handleCitationClick, selectedCitation, conversationId, messageId, isXS, showConfirmationModal, isUser, isError, isDeleted, isSelectable, usesAndroidProseLayout, isStreaming, messageLayout, nativeHeading, iosSelectableParagraphStyle, mergedBlockBaseTextStyle, nativeProseTextStyle, projectSourceReference, workArtifactReference, addToast, compactWorkText]);

  const streamingCursorStyle = useMemo(() => ({
    ...(Platform.OS === 'web' && { display: 'inline' }),
    marginLeft: 4,
    color: colors.text_color_secondary,
    fontSize: compactWorkText || Platform.OS === 'macos' ? markdownStyles.text.fontSize : 17,
    fontFamily: FONT_FAMILY,
  }), [colors, compactWorkText, markdownStyles.text.fontSize]);
  const streamingCursor = useMemo(() => isStreaming
    ? <StreamingMessageCursor style={streamingCursorStyle} selectionActiveRef={selectionActiveRef} />
    : null, [isStreaming, streamingCursorStyle]);

  // Shown when markdown rendering throws, where being able to copy the text out
  // matters more than usual.
  const plainTextFallback = useMemo(() => (
    Platform.OS === 'web'
      ? <Text style={markdownStyles.text}>{content}</Text>
      : (
        <NativeSelectableParagraph
          textStyle={Platform.OS === 'ios' ? iosSelectableParagraphStyle : markdownStyles.text}
        >
          {content}
        </NativeSelectableParagraph>
      )
  ), [content, markdownStyles.text, iosSelectableParagraphStyle]);

  return (
    <MessageSelectionView ref={containerRef} style={styles.container}
      {...(Platform.OS === 'macos' ? {
        conversationId, messageId, isStreaming, theme,
        selectionEnabled: !isUser && !isError && !isDeleted,
      } : {})}>
      <MarkdownErrorBoundary fallback={plainTextFallback}>
        <MarkdownContent
          markdownStyles={markdownStyles}
          markdownRules={markdownRules}
          content={content}
          isStreaming={isStreaming}
          cardPlaceholdersEnabled={!isUser && !isError && !isDeleted}
          streamingCursor={streamingCursor}
        />
      </MarkdownErrorBoundary>
    </MessageSelectionView>
  );
};

MessageContentRenderer.propTypes = {
  content: PropTypes.string.isRequired,
  theme: PropTypes.shape({
    colors: PropTypes.object.isRequired,
    theme: PropTypes.string.isRequired,
  }).isRequired,
  isStreaming: PropTypes.bool,
  messageLayout: PropTypes.string,
  isUser: PropTypes.bool,
  isError: PropTypes.bool,
  isDeleted: PropTypes.bool,
  messageId: PropTypes.string,
  conversationId: PropTypes.string,
};

const styles = StyleSheet.create({
  container: {
    flexGrow: 1,
    ...(Platform.OS === 'web' ? { flexShrink: 1 } : { flexShrink: 0 }),
    minWidth: 0,
    position: 'relative',
    userSelect: Platform.OS === 'web' ? 'text' : undefined,
    ...(Platform.OS === 'web' && {
      wordBreak: 'break-word',
      overflowWrap: 'break-word',
      overflow: 'hidden',
      maxWidth: '100%',
    }),
    ...(Platform.OS !== 'web' && {
      maxWidth: '100%',
    }),
  },
});

export default React.memo(MessageContentRenderer, (prevProps, nextProps) => {
  return (
    prevProps.content === nextProps.content &&
    prevProps.theme.theme === nextProps.theme.theme &&
    prevProps.isStreaming === nextProps.isStreaming &&
    prevProps.messageLayout === nextProps.messageLayout &&
    prevProps.isUser === nextProps.isUser &&
    prevProps.isError === nextProps.isError &&
    prevProps.isDeleted === nextProps.isDeleted &&
    // Use reference comparison instead of JSON.stringify for better performance
    // Colors object reference only changes when theme changes
    prevProps.theme.colors === nextProps.theme.colors &&
    prevProps.messageId === nextProps.messageId &&
    prevProps.conversationId === nextProps.conversationId
  );
});
